import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Chip, 
  Button, 
  Grid,
  Card,
  CardContent,
  CardActions,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  CardHeader,
  CardMedia,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  useMediaQuery,
  useTheme,
  Tooltip,
  LinearProgress,
  BottomNavigation,
  BottomNavigationAction,
  Fade,
  Slide,
  Skeleton,
  Container,
  Fab
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Share as ShareIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  FileCopy as FileCopyIcon,
  Mic as MicIcon,
  Refresh as RefreshIcon,
  AutoFixHigh as AutoFixHighIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  ArrowForward as ArrowForwardIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Article as ArticleIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Accessibility as AccessibilityIcon,
  AccountTree as AccountTreeIcon,
  Business as BusinessIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useApp } from '../contexts/AppContext';
import meetingService from '../services/meetingService';
import userService from '../services/userService';
import transcriptionService from '../services/transcriptionService';
import summaryService from '../services/summaryService';
import systemLogService from '../services/systemLogService';

import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import { doc, onSnapshot, deleteField, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import TranscriptionView from '../components/TranscriptionView';
import { getAppSettings } from '../firebase/services/appSettingsService';
import MDEditor from '@uiw/react-md-editor';
import remarkGfm from 'remark-gfm';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { markdownToHtml, htmlToMarkdown } from '../utils/markdownTiptap';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import modelsService from '../services/modelsService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getUserProfile } from '../firebase/services/userService';
import EmailDialog from '../components/common/EmailDialog';
import EditMeetingDialog from '../components/common/EditMeetingDialog';
import ProjectMeetingsSelectionDialog from '../components/common/ProjectMeetingsSelectionDialog';
import { primaryGradient } from '../theme';
import ChatBot from '../components/ChatBot';
import BaseDialog from '../components/common/BaseDialog';
import BaseButton from '../components/common/BaseButton';

// Animations pour les effets visuels premium
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const titleScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const iconRotate = keyframes`
  from {
    transform: rotate(0deg) scale(0);
    opacity: 0;
  }
  to {
    transform: rotate(360deg) scale(1);
    opacity: 1;
  }
`;

// HeroSection réduite pour contenir seulement le header
const MeetingHeroSection = styled(Box)(({ theme }) => ({
  background: primaryGradient,
  padding: theme.spacing(2, 0), // Réduit de 6,0,4 à 2,0
  position: 'relative',
  marginLeft: '-24px',
  marginRight: '-24px',
  paddingLeft: '24px',
  paddingRight: '24px',
  borderRadius: '0 0 32px 32px',
  overflow: 'hidden',
  minHeight: '100px', // Réduit de 30vh à 100px
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 20px 40px rgba(63, 81, 181, 0.25), 0 10px 20px rgba(63, 81, 181, 0.15)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '10px',
    background: 'linear-gradient(to bottom, rgba(255,255,255,0.05) 0%, transparent 100%)',
    zIndex: 1
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
      linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)
    `,
    zIndex: 1
  }
}));

// Card pour les informations avec animation
const InfoCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '20px',
  padding: '20px',
  textAlign: 'center',
  animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1)`,
  height: '180px', // Hauteur fixe pour uniformiser tous les blocs
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'hidden', // Empêcher le débordement du contenu
  '&:hover': {
    transform: 'translateY(-4px)',
    background: 'rgba(255,255,255,0.2)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)'
  },
  transition: 'all 0.3s ease'
}));

// Élément décoratif flottant pour la HeroSection
const MeetingFloatingDecoration = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  right: '8%',
  width: '80px',
  height: '80px',
  background: 'linear-gradient(45deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))',
  borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
  transform: 'rotate(45deg)',
  animation: `${floatAnimation} 8s ease-in-out infinite`,
  zIndex: 2,
  opacity: 0.8,
  filter: 'blur(0.5px)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

// Titre animé avec effet de scale et icône
const AnimatedTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  animation: `${titleScale} 1.2s cubic-bezier(0.4, 0, 0.2, 1)`,
  '& .title-icon': {
    animation: `${iconRotate} 1s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both`
  }
}));

// Bouton de retour avec animations modernes
const AnimatedBackButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  background: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '16px',
  color: 'white',
  padding: '12px',
  overflow: 'hidden',
  animation: `${slideInLeft} 0.8s cubic-bezier(0.4, 0, 0.2, 1)`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
    transition: 'left 0.5s ease'
  },
  '&:hover': {
    transform: 'translateX(-5px) scale(1.05)',
    background: 'rgba(255,255,255,0.25)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
    '&::before': {
      left: '100%'
    }
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
}));

// Card pour les participants avec animation
const ParticipantChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(21, 101, 192, 0.1)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(21, 101, 192, 0.2)',
  borderRadius: '20px',
  padding: '6px 12px',
  animation: `${scaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  '&:hover': {
    transform: 'translateY(-2px)',
    background: 'rgba(21, 101, 192, 0.15)',
    boxShadow: '0 4px 12px rgba(21, 101, 192, 0.2)'
  },
  transition: 'all 0.2s ease'
}));

// Composant pour la virtualisation de la transcription
const VirtualizedTranscription = ({ utterances, speakerMapping, meeting, onEditSpeaker, containerRef, isMobile }) => {
  const ITEMS_PER_PAGE = 20;
  const ITEM_HEIGHT = 120; // Hauteur estimée par utterance
  
  // Initialisation intelligente de la plage visible
  const initialRange = useMemo(() => {
    return utterances.length <= ITEMS_PER_PAGE 
      ? { start: 0, end: utterances.length }
      : { start: 0, end: Math.min(ITEMS_PER_PAGE, utterances.length) };
  }, [utterances.length, ITEMS_PER_PAGE]);
  
  const [visibleRange, setVisibleRange] = useState(initialRange);
  const [loading, setLoading] = useState(false);
  const [speakerMenuAnchor, setSpeakerMenuAnchor] = useState(null);
  const [selectedUtterance, setSelectedUtterance] = useState(null);
  const [playingUtterance, setPlayingUtterance] = useState(null);
  const [loadingUtterance, setLoadingUtterance] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);

  // Mettre à jour la plage visible quand les utterances changent
  useEffect(() => {
    const newRange = utterances.length <= ITEMS_PER_PAGE 
      ? { start: 0, end: utterances.length }
      : { start: 0, end: Math.min(ITEMS_PER_PAGE, utterances.length) };
    
    setVisibleRange(newRange);
  }, [utterances.length, ITEMS_PER_PAGE]);

  // Nettoyer les états audio lors du démontage du composant
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
      setPlayingUtterance(null);
      setLoadingUtterance(null);
    };
  }, [currentAudio]);

  // Chargement progressif lors du scroll
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const totalHeight = utterances.length * ITEM_HEIGHT;
    
    // Si on a peu d'utterances, on affiche tout sans virtualisation
    if (utterances.length <= ITEMS_PER_PAGE) {
      const newRange = { start: 0, end: utterances.length };
      if (visibleRange.start !== newRange.start || visibleRange.end !== newRange.end) {
        setVisibleRange(newRange);
      }
      return;
    }
    
    // Calculer la range visible avec un buffer
    const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 5);
    const endIndex = Math.min(
      utterances.length,
      Math.ceil((scrollTop + containerHeight) / ITEM_HEIGHT) + 10
    );
    
    // Éviter les mises à jour inutiles si la range n'a pas vraiment changé
    const significantChange = Math.abs(startIndex - visibleRange.start) > 2 || 
                             Math.abs(endIndex - visibleRange.end) > 2;
    
    // Chargement progressif quand on approche de la fin
    if (endIndex > visibleRange.end - 5 && visibleRange.end < utterances.length && !loading) {
      setLoading(true);
      setTimeout(() => {
        setVisibleRange(prev => ({
          start: Math.max(0, startIndex),
          end: Math.min(utterances.length, prev.end + ITEMS_PER_PAGE)
        }));
        setLoading(false);
      }, 100);
    } else if (significantChange) {
      setVisibleRange({ start: startIndex, end: endIndex });
    }
  }, [utterances.length, visibleRange, loading]);

  // Fonction pour formater le timestamp
  const formatTimestamp = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Fonction pour obtenir le nom du speaker
  const getSpeakerName = (speaker) => {
    const mappedName = speakerMapping[speaker];
    if (mappedName === 'autre') {
      return speakerMapping[`custom_${speaker}`] || speaker;
    }
    return mappedName || speaker;
  };

  // Rendu des utterances visibles
  const visibleUtterances = utterances.slice(visibleRange.start, visibleRange.end);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        height: { xs: '50vh', sm: '70vh' }, // Réduit sur mobile
        overflowY: 'auto',
        position: 'relative',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '10px',
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      }}
    >
      {/* Spacer pour maintenir la hauteur totale */}
      <Box sx={{ height: `${visibleRange.start * ITEM_HEIGHT}px` }} />
      
      {visibleUtterances.map((utterance, index) => {
        const actualIndex = visibleRange.start + index;
        const speakerName = getSpeakerName(utterance.speaker);
        
        return (
          <Slide 
            key={actualIndex} 
            direction="up" 
            in={true} 
            style={{ transitionDelay: `${index * 50}ms` }}
          >
            <Card
              sx={{
                mb: 2,
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                overflow: 'visible',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `hsl(${(utterance.speaker.charCodeAt(0) * 137.5) % 360}, 70%, 50%)`,
                      width: 32,
                      height: 32,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {speakerName.charAt(0).toUpperCase()}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography
                        variant={isMobile ? "body2" : "subtitle1"}
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {speakerName}
                      </Typography>
                      
                      <Chip
                        label={formatTimestamp(utterance.start)}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.65rem',
                          height: 24,
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          borderColor: 'primary.light',
                        }}
                      />
                      
                      {/* Bouton play/pause pour écouter l'utterance */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Si cet utterance est en cours de lecture, on pause
                          if (playingUtterance === actualIndex) {
                            if (currentAudio) {
                              currentAudio.pause();
                              setPlayingUtterance(null);
                              setCurrentAudio(null);
                            }
                            return;
                          }
                          
                          // Empêcher le clic si déjà en cours de chargement
                          if (loadingUtterance === actualIndex) {
                            return;
                          }
                          
                          // Arrêter l'audio précédent s'il existe
                          if (currentAudio) {
                            currentAudio.pause();
                            setCurrentAudio(null);
                            setPlayingUtterance(null);
                          }
                          
                          if (meeting?.audioUrl && utterance.start && utterance.end) {
                            // Démarrer l'état de chargement
                            setLoadingUtterance(actualIndex);
                            
                            const audio = new Audio();
                            const startTime = utterance.start / 1000;
                            const endTime = utterance.end / 1000;
                            
                            audio.src = window.MediaSource
                              ? `${meeting.audioUrl}#t=${startTime},${endTime}`
                              : meeting.audioUrl;
                            
                            // Événement quand l'audio commence vraiment à jouer
                            audio.addEventListener('playing', () => {
                              setLoadingUtterance(prev => prev === actualIndex ? null : prev);
                              setPlayingUtterance(actualIndex);
                              setCurrentAudio(audio);
                            });
                            
                            // Événement de sauvegarde au cas où 'playing' ne se déclenche pas
                            audio.addEventListener('canplay', () => {
                              setLoadingUtterance(prev => {
                                if (prev === actualIndex) {
                                  setPlayingUtterance(actualIndex);
                                  setCurrentAudio(audio);
                                  return null;
                                }
                                return prev;
                              });
                            });
                            
                            // En cas d'erreur de chargement
                            audio.addEventListener('error', () => {

                              setLoadingUtterance(null);
                            });
                              
                            if (!window.MediaSource) {
                              audio.addEventListener('loadedmetadata', () => {
                                audio.currentTime = startTime;
                              });
                              audio.addEventListener('timeupdate', () => {
                                if (audio.currentTime >= endTime) {
                                  audio.pause();
                                  setPlayingUtterance(null);
                                  setCurrentAudio(null);
                                }
                              });
                            }
                            
                            // Événements pour gérer la fin de lecture
                            audio.addEventListener('ended', () => {
                              setPlayingUtterance(null);
                              setCurrentAudio(null);
                            });
                            
                            audio.addEventListener('pause', () => {
                              if (audio.ended || audio.currentTime >= endTime) {
                                setPlayingUtterance(null);
                                setCurrentAudio(null);
                              }
                            });
                            
                            // Démarrer le chargement et la lecture
                            audio.play().then(() => {
                              // Si l'audio commence à jouer, s'assurer que les états sont corrects
                              setTimeout(() => {
                                setLoadingUtterance(prev => prev === actualIndex ? null : prev);
                                setPlayingUtterance(actualIndex);
                                setCurrentAudio(audio);
                              }, 100);
                            }).catch((error) => {

                              setLoadingUtterance(prev => prev === actualIndex ? null : prev);
                              setPlayingUtterance(null);
                              setCurrentAudio(null);
                            });
                          }
                        }}
                        sx={{
                          bgcolor: loadingUtterance === actualIndex
                            ? 'rgba(255, 152, 0, 0.08)'
                            : playingUtterance === actualIndex 
                            ? 'rgba(244, 67, 54, 0.08)' 
                            : 'rgba(25, 118, 210, 0.08)',
                          color: loadingUtterance === actualIndex
                            ? '#ff9800'
                            : playingUtterance === actualIndex 
                            ? '#f44336' 
                            : 'primary.main',
                          '&:hover': {
                            bgcolor: loadingUtterance === actualIndex
                              ? 'rgba(255, 152, 0, 0.15)'
                              : playingUtterance === actualIndex 
                              ? 'rgba(244, 67, 54, 0.15)' 
                              : 'rgba(25, 118, 210, 0.15)',
                            transform: loadingUtterance === actualIndex ? 'none' : 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        disabled={!meeting?.audioUrl || loadingUtterance === actualIndex}
                      >
                        {loadingUtterance === actualIndex ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : playingUtterance === actualIndex ? (
                          <PauseIcon fontSize="small" />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                      
                      {/* Bouton modifier pour identifier le participant */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setSpeakerMenuAnchor(e.currentTarget);
                          setSelectedUtterance(utterance);
                        }}
                        sx={{
                          bgcolor: 'rgba(99, 102, 241, 0.08)',
                          color: '#6366f1',
                          '&:hover': {
                            bgcolor: 'rgba(99, 102, 241, 0.15)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.6,
                        color: 'text.primary',
                        fontSize: '1rem',
                      }}
                    >
                      {utterance.text}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        );
      })}
      
      {/* Spacer pour maintenir la hauteur totale */}
      <Box sx={{ height: `${(utterances.length - visibleRange.end) * ITEM_HEIGHT}px` }} />
      
      {/* Indicateur de chargement */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      {/* Menu pour identifier les participants */}
      <Menu
        anchorEl={speakerMenuAnchor}
        open={Boolean(speakerMenuAnchor)}
        onClose={() => {
          setSpeakerMenuAnchor(null);
          setSelectedUtterance(null);
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 200,
            maxWidth: 280,
            maxHeight: 350,
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.1)',
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              py: 1,
              px: 2,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:last-child': {
                borderBottom: 'none',
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            style: {
              maxHeight: '50vh',
              overflow: 'auto',
            },
          },
        }}
      >
        {/* Option "Non identifié" */}
        <MenuItem
          onClick={() => {
            if (onEditSpeaker && selectedUtterance) {
              onEditSpeaker(selectedUtterance.speaker, selectedUtterance.speaker);
            }
            setSpeakerMenuAnchor(null);
            setSelectedUtterance(null);
          }}
          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
        >
          Non identifié
        </MenuItem>
        
        {/* Options des participants */}
        {(meeting?.participants || []).map((participant, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              if (onEditSpeaker && selectedUtterance) {
                onEditSpeaker(selectedUtterance.speaker, participant.displayName);
              }
              setSpeakerMenuAnchor(null);
              setSelectedUtterance(null);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.75rem',
                bgcolor: `hsl(${(participant.displayName.charCodeAt(0) * 137.5) % 360}, 70%, 50%)`,
              }}
            >
              {participant.displayName.charAt(0).toUpperCase()}
            </Avatar>
            {participant.displayName}
          </MenuItem>
        ))}
        
        {/* Option "Autre..." */}
        <MenuItem
          onClick={() => {
            const customName = window.prompt('Nom du participant :');
            if (customName && onEditSpeaker && selectedUtterance) {
              onEditSpeaker(selectedUtterance.speaker, 'autre', customName);
            }
            setSpeakerMenuAnchor(null);
            setSelectedUtterance(null);
          }}
          sx={{ fontStyle: 'italic', color: 'primary.main' }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Autre...
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Panneau pour afficher le contenu selon l'onglet sélectionné
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`meeting-tabpanel-${index}`}
      aria-labelledby={`meeting-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
};

// Nouveau composant StatCard pour les statistiques (inspiré de la page d'accueil)
const MeetingStatCard = styled(Card)(({ theme, color = '#3F51B5' }) => ({
  background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(248,250,255,0.9) 100%)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: '20px',
  boxShadow: '0 8px 24px rgba(63, 81, 181, 0.08), 0 4px 12px rgba(63, 81, 181, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  height: '140px',
  cursor: 'default',
  [theme.breakpoints.down('sm')]: {
    height: '120px' // Réduit la hauteur sur mobile pour éviter le chevauchement
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1), transparent, rgba(255,255,255,0.1))',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(63, 81, 181, 0.12), 0 6px 16px rgba(63, 81, 181, 0.08)',
    '&::before': {
      opacity: 1,
    }
  },
  '& .stat-icon': {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 40,
    height: 40,
    borderRadius: '12px',
    background: `linear-gradient(135deg, ${color}20, ${color}40)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color,
    fontSize: '1.5rem',
    animation: 'floatIcon 3s ease-in-out infinite', // Animation par défaut
    [theme.breakpoints.down('sm')]: {
      width: 28, // Réduit de 40px à 28px sur mobile
      height: 28, // Réduit de 40px à 28px sur mobile
      top: 12, // Réduit légèrement la marge
      left: 12, // Réduit légèrement la marge
      fontSize: '1rem', // Réduit la taille de l'icône de 1.5rem à 1rem
      borderRadius: '8px' // Ajuste le border-radius proportionnellement
    }
  },
  '& .stat-content': {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5) // Réduit légèrement le padding sur mobile
    }
  },
  '& .stat-label': {
    fontSize: '0.875rem',
    color: '#666',
    fontWeight: 500,
    marginBottom: '4px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem', // Réduit la taille du label sur mobile
      marginBottom: '2px'
    }
  },
  '& .stat-value': {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#333',
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem' // Réduit légèrement la taille de la valeur sur mobile
    }
  }
}));

// Section des statistiques
const StatsSection = styled(Box)(({ theme }) => ({
  margin: '16px auto 24px', // Changé de -40px à 16px pour éviter le chevauchement
  padding: '0 24px',
  maxWidth: theme.breakpoints.values.lg,
  opacity: 0,
  animation: 'fadeInUp 0.6s ease-out 0.2s forwards',
  position: 'relative', // Assurer que les enfants positionnés absolument fonctionnent
  zIndex: 10, // Augmenter le z-index pour passer au-dessus du contenu principal
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  '@keyframes floatIcon': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-6px)' }
  }
}));

// Composant CountUp pour animer les chiffres
const CountUpNumber = ({ value, duration = 2000, formatter }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!value || value === 0) return;
    
    let startTime;
    const startValue = 0;
    const endValue = typeof value === 'number' ? value : parseInt(value) || 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutCubic);
      
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <span>{formatter ? formatter(count) : count.toLocaleString()}</span>;
};

// Composant DurationCard avec animation count-up
const DurationCard = ({ durationSeconds }) => {
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds < 0) return '00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.round(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <MeetingStatCard color="#4ECDC4">
      <Box 
        className="stat-icon"
        sx={{ 
          animation: 'floatIcon 3s ease-in-out infinite',
          animationDelay: '0.5s'
        }}
      >
        <AccessTimeIcon />
      </Box>
      <CardContent className="stat-content">
        <Typography className="stat-label">
          Durée
        </Typography>
        <Typography className="stat-value">
          <CountUpNumber 
            value={durationSeconds || 0} 
            duration={1500}
            formatter={formatDuration}
          />
        </Typography>
      </CardContent>
    </MeetingStatCard>
  );
};

// Composant StatusCard amélioré avec deux badges et couleurs corrigées
const StatusCard = ({ isTranscribed, isSummarized, isMobile = false }) => {
  return (
    <MeetingStatCard color="#FF6B6B">
      <Box 
        className="stat-icon"
        sx={{ 
          animation: 'floatIcon 3s ease-in-out infinite',
          animationDelay: '1.5s'
        }}
      >
        <DescriptionIcon />
      </Box>
      <CardContent className="stat-content">
        <Typography className="stat-label" sx={{ mb: 1 }}>
          Statut
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 0.5 
        }}>
          {isTranscribed ? (
            <Chip 
              icon={<CheckCircleIcon fontSize="small" sx={{ color: '#00C853 !important' }} />}
              label={isMobile ? "" : "Transcription effectuée"}
              size="small" 
              sx={{ 
                fontSize: '0.65rem', 
                height: 22,
                color: '#00C853',
                bgcolor: '#E8F5E8',
                border: '1px solid #00C853',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: '#00C853 !important'
                }
              }}
            />
          ) : (
            <Chip 
              icon={<CircularProgress size={10} sx={{ color: '#2196f3 !important' }} />}
              label={isMobile ? "" : "En cours"}
              size="small" 
              sx={{ 
                fontSize: '0.65rem', 
                height: 22,
                color: '#2196f3',
                bgcolor: 'rgba(33, 150, 243, 0.1)',
                border: '1px solid #2196f3',
                '& .MuiChip-icon': {
                  color: '#2196f3 !important'
                }
              }}
            />
          )}
          {isSummarized && (
            <Chip 
              icon={<AutoAwesomeIcon fontSize="small" sx={{ color: '#FF9800 !important' }} />}
              label={isMobile ? "" : "Résumé généré"}
              size="small" 
              sx={{ 
                fontSize: '0.65rem', 
                height: 22,
                color: '#FF9800',
                bgcolor: '#FFF8E1',
                border: '1px solid #FF9800',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: '#FF9800 !important'
                }
              }}
            />
          )}
        </Box>
      </CardContent>
    </MeetingStatCard>
  );
};

// Composant ParticipantsCard avec accordion et animation count-up
const ParticipantsCard = ({ participants = [] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box sx={{ position: 'relative', zIndex: 0 }}>
      <MeetingStatCard 
        color="#FFA726" 
        onClick={() => setIsOpen(!isOpen)}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 32px rgba(63, 81, 181, 0.12), 0 6px 16px rgba(63, 81, 181, 0.08)',
          }
        }}
      >
        <Box 
          className="stat-icon"
          sx={{ 
            animation: 'floatIcon 3s ease-in-out infinite',
            animationDelay: '1s'
          }}
        >
          <PersonIcon />
        </Box>
        <CardContent className="stat-content">
          <Typography className="stat-label">
            Participants
          </Typography>
          <Typography className="stat-value">
            <CountUpNumber value={participants.length} duration={1500} />
          </Typography>
          <Box sx={{ 
            position: 'absolute', 
            bottom: 8, 
            right: 8, 
            color: '#FFA726',
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            <ExpandMoreIcon fontSize="small" />
          </Box>
        </CardContent>
      </MeetingStatCard>
      
      {/* Liste déroulante des participants */}
      <Fade in={isOpen} timeout={300}>
        <Paper
          sx={{
            position: 'absolute',
            top: '150px',
            left: 0,
            right: 0,
            zIndex: 1300, // Augmenté de 10 à 1300 (valeur standard Material-UI pour les dialogs)
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            maxHeight: '200px',
            overflow: 'auto',
            display: isOpen ? 'block' : 'none',
            backgroundColor: '#ffffff'
          }}
        >
          <List dense sx={{ py: 1 }}>
            {participants.length > 0 ? (
              participants.map((participant, index) => {
                const participantName = typeof participant === 'string' 
                  ? participant 
                  : (participant?.displayName || participant?.title || 'Participant');
                
                return (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Avatar sx={{
                        width: 24,
                        height: 24,
                        fontSize: '0.7rem',
                        bgcolor: `hsl(${(participantName.charCodeAt(0) * 137.5) % 360}, 70%, 50%)`
                      }}>
                        {participantName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {participantName}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })
            ) : (
              <ListItem>
                <ListItemText 
                  primary="Aucun participant" 
                  primaryTypographyProps={{ 
                    variant: 'body2', 
                    color: 'text.secondary',
                    style: { fontStyle: 'italic' }
                  }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Fade>
    </Box>
  );
};

// Composant principal
const MeetingDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, setLoading, setError } = useApp();
  const { currentUser, loading: authLoading } = useAuth();
  const { 
    addNotification, 
    removeProgressNotifications,
    addCopyNotification,
    addExportNotification,
    addEmailNotification,
    addDeleteNotification,
    addCancelNotification,
    addActionErrorNotification
  } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [meeting, setMeeting] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({ to: '', subject: '', message: '' });
  // États pour le nouveau dialog d'email
  const [summaryEmailDialogOpen, setSummaryEmailDialogOpen] = useState(false);
  const [transcriptEmailDialogOpen, setTranscriptEmailDialogOpen] = useState(false);

  const [generateSummaryData, setGenerateSummaryData] = useState({ model: 'gpt-3.5-turbo' });

  const [anchorEl, setAnchorEl] = useState(null);
  const [processingAction, setProcessingAction] = useState('');
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [unsubscribeMeeting, setUnsubscribeMeeting] = useState(null);
  const [unsubscribeTranscription, setUnsubscribeTranscription] = useState(null);
  const [unsubscribeSummary, setUnsubscribeSummary] = useState(null);
  const [speakerMapping, setSpeakerMapping] = useState({});
  const [savingMapping, setSavingMapping] = useState(false);
  const [defaultSummaryModel, setDefaultSummaryModel] = useState('gpt-3.5-turbo');
  const [tiptapContent, setTiptapContent] = useState('');
  const [summaryPrompt, setSummaryPrompt] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [showAllShared, setShowAllShared] = useState(false);
  
  // Référence pour le conteneur de transcription
  const transcriptionContainerRef = useRef(null);
  
  // État pour la modale d'identification des locuteurs (maintenant intégrée dans la dialog unifiée)
  const [currentPlayingSpeaker, setCurrentPlayingSpeaker] = useState(null);
  const [tempSpeakerMapping, setTempSpeakerMapping] = useState({});
  const audioRef = useRef(null);
  const [audioSrc, setAudioSrc] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Ajouter une référence pour stocker les extraits audio préchargés
  const preloadedAudioRefs = useRef({});
  
  // Ajouter un état pour suivre le statut du préchargement
  const [preloadedAudioStatus, setPreloadedAudioStatus] = useState({});
  
  // 1. Préchargement global des extraits audio dès que la transcription est chargée
  window.globalSpeakerSamplesCache = window.globalSpeakerSamplesCache || { current: {} };
  const globalSpeakerSamplesCache = window.globalSpeakerSamplesCache;
  
  // Ajouter l'état pour la progression du préchargement audio
  const [audioPreloadProgress, setAudioPreloadProgress] = useState({ loaded: 0, total: 0 });
  
  // États pour les menus contextuels de la transcription et du résumé
  const [anchorElTranscription, setAnchorElTranscription] = useState(null);
  const [anchorElSummary, setAnchorElSummary] = useState(null);
  
  // État pour le repli des informations en mode mobile
  const [infoExpanded, setInfoExpanded] = useState(false);
  
  // État pour marquer le premier chargement et éviter le bug des onglets
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // État pour la dialog d'édition de la réunion
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // État pour la dialog de confirmation de suppression
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  
  // États pour la nouvelle dialog unifiée de génération de résumé
  const [generateSummaryDialogOpen, setGenerateSummaryDialogOpen] = useState(false);
  const [summaryStep, setSummaryStep] = useState(0);

  // États pour la sélection des réunions précédentes du projet
  const [projectMeetingsDialogOpen, setProjectMeetingsDialogOpen] = useState(false);
  const [selectedPreviousMeetingIds, setSelectedPreviousMeetingIds] = useState([]);
  const [pendingSummaryGeneration, setPendingSummaryGeneration] = useState(null);

  // États pour la gestion des erreurs mobile
  const [mobileAuthError, setMobileAuthError] = useState(null);
  const [showMobileAuthDialog, setShowMobileAuthDialog] = useState(false);
  
  // Détecter si on est sur mobile
  const isMobileDevice = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
  }, []);
  
  // Gestionnaire d'erreurs spécifique au mobile
  useEffect(() => {
    const handleMobileAuthError = (event) => {

      setMobileAuthError({
        type: event.detail.type,
        message: event.detail.message,
        action: event.detail.action,
        timestamp: new Date()
      });
      
      // Afficher la dialog d'erreur mobile
      setShowMobileAuthDialog(true);
    };

    // Écouter les erreurs d'authentification mobile
    window.addEventListener('mobile-auth-error', handleMobileAuthError);
    
    // Vérifier s'il y a une erreur de popup récente au chargement
    if (isMobileDevice) {
      const recentPopupError = localStorage.getItem('mobile_popup_error');
      if (recentPopupError) {
        const errorTime = parseInt(recentPopupError);
        const timeSinceError = Date.now() - errorTime;
        
        // Si l'erreur date de moins de 5 minutes
        if (timeSinceError < 5 * 60 * 1000) {
          setMobileAuthError({
            type: 'popup_blocked',
            message: 'Token Azure non disponible. Veuillez vous reconnecter.',
            action: 'reconnect',
            timestamp: new Date(errorTime)
          });
          setShowMobileAuthDialog(true);
          
          // Nettoyer l'erreur après l'avoir affichée
          localStorage.removeItem('mobile_popup_error');
        }
      }
    }

    return () => {
      window.removeEventListener('mobile-auth-error', handleMobileAuthError);
    };
  }, [isMobileDevice]);

  // Fonction pour gérer la reconnexion sur mobile
  const handleMobileReconnect = () => {
    setShowMobileAuthDialog(false);
    setMobileAuthError(null);
    
    // Nettoyer les caches d'authentification
    localStorage.removeItem('mobile_popup_error');
    localStorage.removeItem('mobile_token_refresh_in_progress');
    localStorage.removeItem('mobile_token_refresh_start_time');
    
    // Rediriger vers la page de connexion
    navigate('/login', { 
      state: { 
        reason: 'mobile_auth_error',
        message: 'Reconnexion nécessaire pour résoudre le problème de token Azure'
      }
    });
  };

  // Met à jour le contenu de l'éditeur quand tiptapContent change
  useEffect(() => {
    // Garder la logique de conversion pour l'affichage
    if (summary && summary.text) {
      markdownToHtml(summary.text).then(html => {
        setTiptapContent(html || '<p><em>Aucun résumé disponible.</em></p>');
      });
    }
  }, [summary]);

  // Basculer automatiquement sur l'onglet résumé si disponible (uniquement au premier chargement)
  useEffect(() => {
    // Si un résumé est disponible et terminé, qu'on est toujours sur l'onglet transcription, et que c'est le premier chargement
    if (summary && summary.status === 'Terminé' && tabValue === 0 && isInitialLoad) {
      // Utiliser un timeout pour éviter les changements d'état simultanés
      const timeoutId = setTimeout(() => {
      setTabValue(1); // Basculer sur l'onglet résumé (index 1)
      setIsInitialLoad(false); // Marquer qu'on n'est plus dans le chargement initial
      }, 100);
      
      return () => clearTimeout(timeoutId);
    } else if (isInitialLoad && (summary === null || summary?.status !== 'Terminé')) {
      // Si pas de résumé ou pas terminé, marquer qu'on n'est plus dans le chargement initial après un délai
      const timeoutId = setTimeout(() => {
        setIsInitialLoad(false);
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [summary?.status, tabValue, isInitialLoad]);

  // Charger les données de la réunion et configurer les écouteurs en temps réel
  useEffect(() => {
    let isMounted = true; // Flag pour éviter les setState après unmount
    
    const fetchMeetingDetails = async () => {
      if (!isMounted) return;
      setIsLoading(true);
      
      try {
        // Récupérer initialement les données de la réunion
        const meetingData = await meetingService.getMeetingById(id);
        
        if (!isMounted) return;
        
        if (!meetingData) {
          setError('Réunion non trouvée');
          navigate('/meetings');
          return;
        }
        
        setMeeting(meetingData);
        
        // Configurer un écouteur en temps réel sur la réunion
        const meetingRef = doc(db, 'meetings', id);
        const unsubscribe = onSnapshot(meetingRef, (docSnapshot) => {
          if (!isMounted) return;
          
          if (docSnapshot.exists()) {
            const updatedMeeting = { id: docSnapshot.id, ...docSnapshot.data() };
            setMeeting(prev => {
              // Éviter les mises à jour inutiles
              if (JSON.stringify(prev) === JSON.stringify(updatedMeeting)) {
                return prev;
              }
              return updatedMeeting;
            });
          } else {
            // La réunion a été supprimée
            addActionErrorNotification('l\'accès à la réunion', 'Cette réunion n\'existe plus');
            navigate('/meetings');
          }
        }, (error) => {

        });
        
        // Sauvegarder la fonction de désabonnement
        setUnsubscribeMeeting(() => unsubscribe);
        
        // Si la réunion a déjà une transcription, configurer un écouteur sur celle-ci
        if (meetingData.transcriptionId && isMounted) {
          setupTranscriptionListener(meetingData.transcriptionId);
          setupSummaryListener(meetingData.transcriptionId);
        }
      } catch (error) {
        if (!isMounted) return;
        setError('Erreur lors du chargement des détails de la réunion');

      } finally {
        if (isMounted) {
        setIsLoading(false);
        }
      }
    };
    
    fetchMeetingDetails();
    
    // Nettoyer les écouteurs lorsque le composant est démonté
    return () => {
      isMounted = false;
      if (unsubscribeMeeting) {
        unsubscribeMeeting();
      }
      if (unsubscribeTranscription) {
        unsubscribeTranscription();
      }
      if (unsubscribeSummary) {
        unsubscribeSummary();
      }
    };
  }, [id, navigate, setError]);
  
  // Configurer les listeners de transcription et résumé quand le meeting change
  useEffect(() => {
    if (meeting?.transcriptionId && meeting.transcriptionId !== transcription?.id) {
      setupTranscriptionListener(meeting.transcriptionId);
      setupSummaryListener(meeting.transcriptionId);
    }
  }, [meeting?.transcriptionId, transcription?.id]);
  
  // Charger le mapping existant si présent dans la transcription
  useEffect(() => {
    if (transcription && transcription.speakerMapping) {
      setSpeakerMapping(transcription.speakerMapping);
    }
  }, [transcription]);
  
  // Charger le modèle par défaut OpenAI pour le résumé ET le prompt système
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const appSettings = await getAppSettings();
        if (appSettings?.openAISettings?.model) {
          setDefaultSummaryModel(appSettings.openAISettings.model);
        }
        if (appSettings?.openAISettings?.summaryPrompt) {
          setSummaryPrompt(appSettings.openAISettings.summaryPrompt);
        }
      } catch (e) {
        // fallback silencieux
      }
    };
    fetchSettings();
  }, []);
  
  // Charger les modèles de prompt disponibles
  useEffect(() => {
    const unsubscribe = modelsService.subscribeToModels((modelsList) => {
      setModels(modelsList);
    });
    return () => unsubscribe();
  }, []);
  
  // Charger les infos des utilisateurs partagés
  useEffect(() => {
    const fetchSharedUsers = async () => {
      if (Array.isArray(meeting?.sharedWith) && meeting.sharedWith.length > 0) {
        const users = await Promise.all(
          meeting.sharedWith.map(uid => getUserProfile(uid).catch(() => null))
        );
        setSharedUsers(users.filter(Boolean));
      } else {
        setSharedUsers([]);
      }
    };
    fetchSharedUsers();
  }, [meeting]);
  
  // Extraire la liste des speakers AssemblyAI détectés
  const detectedSpeakers = React.useMemo(() => {
    if (transcription?.utterances?.length > 0) {
      return Array.from(new Set(transcription.utterances.map(u => u.speaker)));
    }
    if (transcription?.result?.words?.length > 0) {
      return Array.from(new Set(transcription.result.words.map(w => w.speaker)));
    }
    return [];
  }, [transcription]);
  
  // Extraire des extraits pour chaque locuteur
  const getSpeakerSamples = useCallback((speaker) => {
    if (!transcription?.utterances) return [];
    
    // Trouver les 3 premiers segments de ce locuteur avec du texte
    return transcription.utterances
      .filter(u => u.speaker === speaker && u.text && u.text.length > 10)
      .slice(0, 3);
  }, [transcription]);
  
  // Mettre à jour le mapping temporaire quand speakerMapping change
  useEffect(() => {
    setTempSpeakerMapping({...speakerMapping});
  }, [speakerMapping]);
  
  // Ajouter le hook de préchargement ici maintenant que detectedSpeakers et getSpeakerSamples sont définis
  useEffect(() => {
    // On ne précharge les extraits QUE si le résumé n'est pas terminé
    if (
      !transcription?.utterances ||
      !meeting?.audioUrl ||
      (summary && summary.status === 'Terminé')
    ) return;
    const cache = {};
    const promises = [];
    let loaded = 0;
    let total = 0;
    const speakers = Array.from(new Set(transcription.utterances.map(u => u.speaker)));

    speakers.forEach(speaker => {
      const samples = transcription.utterances
        .filter(u => u.speaker === speaker && u.text && u.text.length > 10)
        .slice(0, 3);
      cache[speaker] = samples.map((sample, idx) => {
        const startTime = sample.start / 1000;
        const endTime = sample.end / 1000;
        const audio = new Audio();
        audio.preload = 'auto';
        audio.src = window.MediaSource
          ? `${meeting.audioUrl}#t=${startTime},${endTime}`
          : meeting.audioUrl;
        if (!window.MediaSource) {
          audio.addEventListener('loadedmetadata', () => {
            audio.currentTime = startTime;
          });
          audio.addEventListener('timeupdate', () => {
            if (audio.currentTime >= endTime) {
              audio.pause();
            }
          });
        }
        return { sample, audio, ready: false };
      });
      total += cache[speaker].length;
      cache[speaker].forEach((obj, idx) => {
        promises.push(new Promise(resolve => {
          obj.audio.addEventListener('canplaythrough', () => {
            obj.ready = true;
            loaded += 1;
            setAudioPreloadProgress(prev => ({ ...prev, loaded, total }));
            resolve();
          }, { once: true });
          obj.audio.addEventListener('error', () => {
            loaded += 1;
            setAudioPreloadProgress(prev => ({ ...prev, loaded, total }));
            resolve();
          }, { once: true });
          obj.audio.load();
        }));
      });
    });
    setAudioPreloadProgress({ loaded: 0, total });
    globalSpeakerSamplesCache.current = cache;
    Promise.all(promises).then(() => setPreloadedAudioStatus({ ...preloadedAudioStatus, _global: Date.now() }));
  }, [transcription, meeting, summary]);
  
  // 2. Gestion lecture/stop multi-extrait
  const [currentPlaying, setCurrentPlaying] = useState({ speaker: null, idx: null });
  const playSpeakerSample = (speaker, idx) => {
    stopAudio();
    const obj = globalSpeakerSamplesCache.current[speaker]?.[idx];
    if (obj && obj.ready) {
      audioRef.current = obj.audio;
      obj.audio.play().then(() => {
        setIsPlaying(true);
        setCurrentPlaying({ speaker, idx });
      }).catch(() => {
        setIsPlaying(false);
        setCurrentPlaying({ speaker: null, idx: null });
      });
      // Correction : bien réinitialiser le bouton à la fin de la lecture
      obj.audio.onended = () => {
        setIsPlaying(false);
        setCurrentPlaying({ speaker: null, idx: null });
      };
      // Si l'utilisateur arrête manuellement (pause), on réinitialise aussi
      obj.audio.onpause = () => {
        if (!obj.audio.ended) {
          setIsPlaying(false);
          setCurrentPlaying({ speaker: null, idx: null });
        }
      };
    }
  };
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      setCurrentPlaying({ speaker: null, idx: null });
    }
  };
  
  // Fonction pour vérifier si les locuteurs ont été personnalisés (noms différents des défauts)
  const haveSpeakersBeenCustomized = useCallback(() => {
    // Vérifier si la réunion et la transcription existent
    if (!meeting || !transcription || !transcription.utterances || transcription.utterances.length === 0) {
      return false;
    }
    
    // Vérifier les clés du speakerMapping (les speakers originaux AssemblyAI comme A, B, C)
    const originalSpeakers = Object.keys(speakerMapping).filter(key => !key.startsWith('custom_'));
    
    // Vérifier si au moins un speaker original a été mappé vers un nom personnalisé
    const customizedSpeakers = originalSpeakers.filter(originalSpeaker => {
      const mapping = speakerMapping[originalSpeaker];
      const customMapping = speakerMapping[`custom_${originalSpeaker}`];
      
      // Si il y a un mapping personnalisé (custom_)
      if (customMapping && customMapping.trim() !== '') {
        return true;
      }
      
      // Si le mapping est vers un participant (pas le speaker original et pas "autre")
      if (mapping && mapping !== originalSpeaker && mapping !== 'autre') {
        return true;
      }
      
      return false;
    });
    
    // Debug pour voir ce qui se passe
    console.log('Debug haveSpeakersBeenCustomized (FIXED):', {
      originalSpeakers,
      speakerMapping,
      customizedSpeakers,
      result: customizedSpeakers.length > 0
    });
    
    // Si au moins un locuteur original est personnalisé, considérer qu'ils ont été définis
    return customizedSpeakers.length > 0;
  }, [meeting, transcription, speakerMapping]);
  
  // Fonction pour vérifier si les locuteurs doivent être identifiés
  const shouldIdentifySpeakers = useCallback(() => {
    // Vérifier si la réunion et la transcription existent
    if (!meeting || !transcription || !transcription.utterances || transcription.utterances.length === 0) {
      return false;
    }
    
    // Si aucun participant n'est défini, ne pas afficher la modale
    if (!Array.isArray(meeting.participants) || meeting.participants.length === 0) {
      return false;
    }
    
    // Vérifier si tous les locuteurs ont déjà un mapping
    const allSpeakers = Array.from(new Set(transcription.utterances.map(u => u.speaker)));
    
    // Si tous les locuteurs sont mappés à un participant ou une valeur personnalisée
    const allMapped = allSpeakers.every(speaker => 
      speakerMapping[speaker] && 
      (speakerMapping[speaker] !== speaker || speakerMapping[`custom_${speaker}`])
    );
    
    return !allMapped;
  }, [meeting, transcription, speakerMapping]);
  
  // Nouveau handler pour ouvrir la dialog unifiée
  const handleOpenSummaryDialog = () => {
    // Trouver le modèle par défaut "Réunion sans modèle particulier"
    const defaultModel = models.find(m => m.title === "Réunion sans modèle particulier");
    const modelToUse = defaultModel ? defaultModel.id : defaultSummaryModel;
    
    // Debug pour vérifier la sélection du modèle par défaut
    console.log('Debug modèle par défaut:', {
      models: models.map(m => ({ id: m.id, title: m.title })),
      defaultModel,
      modelToUse,
      defaultSummaryModel
    });
    
    // Si les locuteurs ont été personnalisés, aller directement aux modèles de prompt
    if (haveSpeakersBeenCustomized()) {
      setSummaryStep(1); // Étape sélection du modèle directement
      setGenerateSummaryData({ model: modelToUse });
      // Sélectionner automatiquement le modèle par défaut dans l'interface
      if (defaultModel) {
        setSelectedModel(defaultModel);
      }
    } else if (shouldIdentifySpeakers()) {
      setSummaryStep(0); // Étape identification des locuteurs
      setGenerateSummaryData({ model: modelToUse });
      // Préparer le modèle par défaut pour l'étape suivante
      if (defaultModel) {
        setSelectedModel(defaultModel);
      }
    } else {
      setSummaryStep(1); // Étape sélection du modèle directement
      setGenerateSummaryData({ model: modelToUse });
      // Sélectionner automatiquement le modèle par défaut dans l'interface
      if (defaultModel) {
        setSelectedModel(defaultModel);
      }
    }
    
    setGenerateSummaryDialogOpen(true);
    handleCloseMenu();
  };
  
  // Gérer le changement de mapping dans la modale
  const handleTempMappingChange = (speaker, participantId) => {
    setTempSpeakerMapping(prev => ({ ...prev, [speaker]: participantId }));
  };
  
  // Handler pour édition du speaker dans la transcription
  const handleEditSpeaker = async (speakerKey, newValue, customValue) => {
    const newMapping = { ...speakerMapping, [speakerKey]: newValue };
    if (newValue === 'autre') {
      newMapping[`custom_${speakerKey}`] = customValue;
    } else {
      delete newMapping[`custom_${speakerKey}`];
    }
    setSpeakerMapping(newMapping);
    try {
      await transcriptionService.updateSpeakerMapping(transcription.id, newMapping);
      // Mise à jour des utterances si le nom du participant change (hors 'autre')
      if (transcription?.id && newValue && newValue !== 'autre') {
        await transcriptionService.updateParticipantNameInUtterances(
          transcription.id,
          speakerKey,
          newValue
        );
      }
      await addNotification({
        message: '✅ Attribution des locuteurs modifiée avec succès',
        type: 'success',
        category: 'transcription',
        metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
      });
    } catch (e) {

      await addActionErrorNotification('la modification de l\'attribution des locuteurs', e.message || 'Erreur inconnue');
    }
  };
  
  // Fonction utilitaire pour obtenir les initiales à partir du nom complet
  function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  
  // Fonction utilitaire pour formater la durée en hh:mm:ss ou mm:ss
  function formatDuration(seconds) {
    if (!seconds || isNaN(seconds) || seconds < 0) return 'Non disponible';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.round(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  // Fonction pour calculer le coût de transcription AssemblyAI
  const calculateTranscriptionCost = (durationSeconds) => {
    if (!durationSeconds || durationSeconds <= 0) return null;
    
    const ASSEMBLY_AI_RATE = 0.27; // $0.27 par heure
    const durationHours = durationSeconds / 3600;
    const costUSD = durationHours * ASSEMBLY_AI_RATE;
    const costEUR = costUSD * 0.85; // Approximation du taux de change
    
    return {
      usd: costUSD,
      eur: costEUR,
      durationHours
    };
  };

  // Composant pour afficher les coûts de manière élégante et uniforme
  const CostDisplay = ({ title, cost, tokens, icon, color = 'primary.main', variant = 'outlined', details }) => {
    const theme = useTheme();
    
    if (!cost) return null;
    
    // Définir les couleurs spécifiques pour éviter les problèmes de contraste
    const displayColor = '#1976d2'; // Bleu pour transcription et résumé
    const iconBgColor = '#1976d2';
    
    return (
      <Box>
        <Card 
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: displayColor,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${displayColor}25`
            }
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Box 
                sx={{ 
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: iconBgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 48,
                  minHeight: 48,
                  boxShadow: `0 2px 8px ${iconBgColor}30`
                }}
              >
                {React.cloneElement(icon, { 
                  sx: { 
                    color: 'white', 
                    fontSize: '1.5rem' 
                  } 
                })}
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                  {title}
                </Typography>
                
                <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: displayColor }}>
                    ${(typeof cost === 'number' ? cost : 
                      cost.total_usd || cost.usd || cost.totalUsd || cost.cost_usd || 0).toFixed(4)}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ≈ {(typeof cost === 'number' ? cost * 0.92 : 
                       cost.total_eur || cost.eur || cost.totalEur || cost.cost_eur || 0).toFixed(4)}€
                  </Typography>
                </Stack>
                
                {tokens && (
                  <Chip 
                    label={`${tokens.total || tokens} tokens utilisés`}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      borderColor: displayColor,
                      color: displayColor,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      bgcolor: 'background.paper'
                    }}
                  />
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        {details && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', px: 1 }}>
            {details}
          </Typography>
        )}
      </Box>
    );
  };
  
  // Calcul de la durée à partir des utterances ou words
  const getDurationFromTranscription = (transcription) => {
    if (transcription?.utterances?.length > 0) {
      const first = transcription.utterances[0];
      const last = transcription.utterances[transcription.utterances.length - 1];
      if (last && typeof last.end === 'number' && typeof first.start === 'number') {
        return (last.end - first.start) / 1000; // conversion ms -> s si besoin
      }
    }
    if (transcription?.result?.words?.length > 0) {
      const first = transcription.result.words[0];
      const last = transcription.result.words[transcription.result.words.length - 1];
      if (last && typeof last.end === 'number' && typeof first.start === 'number') {
        return last.end - first.start;
      }
    }
    return null;
  };
  const durationSeconds = getDurationFromTranscription(transcription);
  const formattedDuration = formatDuration(durationSeconds);
  
  // Pour l'autocomplétion des utilisateurs lors du partage
  const [shareUserInput, setShareUserInput] = useState('');
  const [shareUserOptions, setShareUserOptions] = useState([]);
  const [shareSelectedUser, setShareSelectedUser] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);

  // Recherche d'utilisateurs pour l'autocomplete
  useEffect(() => {
    if (shareUserInput.length < 2) {
      setShareUserOptions([]);
      return;
    }
    setShareLoading(true);
    userService.searchUsers(shareUserInput).then(users => {
      setShareUserOptions(users);
      setShareLoading(false);
    }).catch(() => setShareLoading(false));
  }, [shareUserInput]);

  // Fonction de partage : ajoute l'utilisateur à sharedWith
  const handleShareMeeting = async () => {
    if (!shareSelectedUser || !meeting) return;
    setLoading(true);
    try {
      // Mettre à jour la réunion dans Firestore
      const sharedWith = Array.isArray(meeting.sharedWith) ? meeting.sharedWith : [];
      if (sharedWith.includes(shareSelectedUser.id)) {
        try {
          await addNotification({
            message: 'ℹ️ Cette réunion est déjà partagée avec cet utilisateur',
            type: 'error',
            category: 'meeting',
            metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
          });
        } catch (notifError) {

        }
        setLoading(false);
        return;
      }
      // Ajout du champ ownerId/ownerName si absent
      const updateData = {
        sharedWith: [...sharedWith, shareSelectedUser.id],
      };
      if (!meeting.ownerId) {
        updateData.ownerId = meeting.ownerId || meeting.userId || null;
      }
      if (!meeting.ownerName) {
        updateData.ownerName = meeting.ownerName || meeting.createdBy || meeting.createdByName || meeting.title || null;
      }
      await meetingService.updateMeeting(meeting.id, updateData);
              try {
          await addNotification({
            message: `🤝 Réunion partagée avec succès avec ${shareSelectedUser.displayName}`,
            type: 'success',
            category: 'meeting',
            metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
          });
        } catch (notifError) {

        }
      setShareDialogOpen(false);
      setShareSelectedUser(null);
      setShareUserInput('');
          } catch (e) {
              try {
          await addNotification({
            message: '❌ Erreur lors du partage de la réunion',
            type: 'error',
            category: 'meeting',
            metadata: { meetingTitle: meeting.title, meetingId: meeting.id, errorMessage: e.message }
          });
        } catch (notifError) {

        }
    } finally {
      setLoading(false);
    }
  };
  
  // Ajout pour l'envoi d'email via Graph
  const [appSettings, setAppSettings] = useState(null);
  useEffect(() => {
    getAppSettings().then(setAppSettings).catch(() => {});
  }, []);
  
  // Si l'authentification est en cours ou l'utilisateur non authentifié, afficher un loader
  if (authLoading || !currentUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Si les données sont en cours de chargement
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Si la réunion n'a pas été trouvée
  if (!meeting) {
    return (
      <Box sx={{ textAlign: 'center', my: 4 }}>
        <Alert severity="error">Réunion non trouvée</Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/meetings')}
          sx={{ mt: 2 }}
        >
          Retour à la liste
        </Button>
      </Box>
    );
  }
  
  // Formater la date
  const formattedDate = meeting.date 
    ? (() => {
        const date = new Date(meeting.date);
        return isValid(date) 
          ? format(date, "d MMM yyyy", { locale: fr }) 
          : 'Date non disponible';
      })()
    : 'Date non disponible';
  
  // Handler pour changer le mapping
  const handleMappingChange = (speaker, participantId) => {
    setSpeakerMapping(prev => ({ ...prev, [speaker]: participantId }));
  };

  // Sauvegarder le mapping dans Firestore
  const handleSaveMapping = async () => {
    if (!transcription?.id) return;
    setSavingMapping(true);
    try {
      await transcriptionService.updateSpeakerMapping(transcription.id, speakerMapping);
      try {
        await addNotification({
          message: '✅ Mapping des locuteurs sauvegardé avec succès',
          type: 'success',
          category: 'transcription',
          metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
        });
      } catch (notifError) {

      }
    } catch (e) {
              try {
          await addNotification({
            message: '❌ Erreur lors de la sauvegarde du mapping des locuteurs',
            type: 'error',
            category: 'transcription',
            metadata: { meetingTitle: meeting.title, meetingId: meeting.id, errorMessage: e.message }
          });
        } catch (notifError) {

      }
    } finally {
      setSavingMapping(false);
    }
  };

  // Fonction pour configurer un écouteur sur la transcription
  function setupTranscriptionListener(transcriptionId) {
    // Éviter de reconfigurer si déjà en place
    if (unsubscribeTranscription && transcription?.id === transcriptionId) {
      return;
    }
    
    // Nettoyer l'écouteur précédent si existant
    if (unsubscribeTranscription) {
      unsubscribeTranscription();
    }
    
    // Configurer un nouvel écouteur
    const transcriptionRef = doc(db, 'transcriptions', transcriptionId);
    const unsubscribe = onSnapshot(transcriptionRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const transcriptionData = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // ===== NOUVELLE LOGIQUE DE VALIDATION =====
        const isReallyCompleted = (data) => {
          if (data.status !== 'completed') return false;
          
          // Vérifier qu'on a au minimum le texte
          const hasText = (data.fullText && data.fullText.trim().length > 0) ||
                          (data.result && data.result.text && data.result.text.trim().length > 0);
          
          // Si diarization était activée, vérifier les utterances
          if (data.enableDiarization || data.diarizationEnabled) {
            const hasUtterances = (data.utterances && Array.isArray(data.utterances) && data.utterances.length > 0) ||
                                 (data.result && data.result.utterances && Array.isArray(data.result.utterances) && data.result.utterances.length > 0);
            return hasText && hasUtterances;
          }
          
          return hasText;
        };
        
        // Traitement du texte de transcription
        let fullText = '';
        let hasIdentifiedSpeakers = false;
        
        // Utiliser fullText en priorité s'il existe déjà
        if (transcriptionData.fullText && transcriptionData.fullText.trim().length > 0) {
          fullText = transcriptionData.fullText;
          hasIdentifiedSpeakers = transcriptionData.hasIdentifiedSpeakers || false;
        } else if (transcriptionData.utterances && Array.isArray(transcriptionData.utterances)) {
          // Format normalisé avec utterances directement dans le document
          fullText = transcriptionData.utterances.map(u => `${u.speaker}: ${u.text}`).join('\n\n');
          hasIdentifiedSpeakers = true;
        } else if (transcriptionData.result && transcriptionData.result.utterances) {
          // Cas où le texte est dans les utterances (format avec diarisation)
          fullText = transcriptionData.result.utterances.map(u => `${u.speaker}: ${u.text}`).join('\n\n');
          hasIdentifiedSpeakers = true;
        } else if (transcriptionData.result && transcriptionData.result.speaker_labels 
                 && transcriptionData.result.words && transcriptionData.result.words.length > 0) {
          // Format AssemblyAI avec diarisation et mots individuels
          const speakerSegments = [];
          let currentSpeaker = null;
          let currentText = '';
          
          // Regrouper les mots par locuteur
          transcriptionData.result.words.forEach(word => {
            if (word.speaker !== currentSpeaker) {
              if (currentSpeaker !== null) {
                speakerSegments.push({ speaker: `Locuteur ${currentSpeaker}`, text: currentText.trim() });
              }
              currentSpeaker = word.speaker;
              currentText = word.text;
            } else {
              currentText += ' ' + word.text;
            }
          });
          
          // Ajouter le dernier segment
          if (currentSpeaker !== null && currentText.trim()) {
            speakerSegments.push({ speaker: `Locuteur ${currentSpeaker}`, text: currentText.trim() });
          }
          
          fullText = speakerSegments.map(segment => `${segment.speaker}: ${segment.text}`).join('\n\n');
          hasIdentifiedSpeakers = true;
        } else if (transcriptionData.result && transcriptionData.result.text) {
          // Cas où le texte est directement disponible dans result.text (format AssemblyAI)
          fullText = transcriptionData.result.text;
        } else if (transcriptionData.result && transcriptionData.result.segments) {
          // Cas où le texte est dans les segments
          fullText = transcriptionData.result.segments.map(s => s.text).join(' ');
        }
        
        // Ajouter les propriétés au transcription data
        transcriptionData.fullText = fullText;
        transcriptionData.hasIdentifiedSpeakers = hasIdentifiedSpeakers;
        
        // ===== VALIDATION ET GESTION DES STATUTS =====
        if (isReallyCompleted(transcriptionData)) {

          transcriptionData.validationStatus = 'completed';
        } else if (transcriptionData.status === 'processing_final') {

          transcriptionData.validationStatus = 'processing_final';
        } else if (transcriptionData.status === 'completed' && !isReallyCompleted(transcriptionData)) {

          transcriptionData.validationStatus = 'processing_final';
        } else {
          // États normaux (processing, error, etc.)
          transcriptionData.validationStatus = transcriptionData.status;
        }
        
        setTranscription(prev => {
          // Éviter les mises à jour inutiles
          if (prev && JSON.stringify(prev) === JSON.stringify(transcriptionData)) {
            return prev;
          }
          return transcriptionData;
        });
        
        // La récupération du résumé est maintenant gérée par setupSummaryListener
      }
    }, (error) => {

    });
    
    // Sauvegarder la fonction de désabonnement
    setUnsubscribeTranscription(() => unsubscribe);
  }

  // Gérer le changement d'onglet
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Marquer qu'on n'est plus dans le chargement initial si l'utilisateur clique manuellement
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  // Gérer l'ouverture du menu actions
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Gérer la fermeture du menu actions
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Gérer l'ouverture de la boîte de dialogue de partage
  const handleOpenShareDialog = () => {
    setShareDialogOpen(true);
    handleCloseMenu();
  };

  // Gérer la fermeture de la boîte de dialogue de partage
  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  // Gérer l'ouverture de la boîte de dialogue d'email pour le résumé
  const handleOpenEmailDialog = async () => {
    if (!summary) {
      await addActionErrorNotification('l\'envoi du résumé par email', 'Aucun résumé disponible pour cette réunion');
      return;
    }
    setSummaryEmailDialogOpen(true);
    setAnchorElSummary(null); // Fermer le menu spécifique au résumé
  };

  // Gérer la fermeture de la boîte de dialogue d'email
  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
  };

  // Gérer la fermeture de la boîte de dialogue d'email pour le résumé
  const handleCloseSummaryEmailDialog = () => {
    setSummaryEmailDialogOpen(false);
  };

  const handleOpenTranscriptEmailDialog = async () => {
    if (!transcription) {
      await addActionErrorNotification('l\'envoi de la transcription par email', 'Aucune transcription disponible');
      return;
    }
    setAnchorElTranscription(null);
    setTranscriptEmailDialogOpen(true);
  };

  const handleCloseTranscriptEmailDialog = () => {
    setTranscriptEmailDialogOpen(false);
  };

  // Remplacer handleSendEmail pour utiliser la fonction cloud
  const handleSendEmail = async () => {
    setLoading(true);
    setProcessingAction('email');
    try {
      // Forcer le rafraîchissement du token Firebase avant l'appel
      const idToken = await window.firebase.auth().currentUser.getIdToken(true); // true = force refresh
      const response = await fetch('https://us-central1-diotranscript.cloudfunctions.net/sendTranscriptionCompletionEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          data: {
            meetingId: meeting.id,
            to: emailData.to,
            subject: emailData.subject,
            message: emailData.message,
            includeTranscription: true,
            includeSummary: !!summary
          }
        })
      });
      const result = await response.json();
      if (result.result && result.result.success) {
        setEmailDialogOpen(false);
        await addEmailNotification(emailData.to);
      } else {
        throw new Error(result.result?.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      setError('Erreur lors de l\'envoi de l\'email');
      await addActionErrorNotification('l\'envoi de l\'email', error.message || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
      setProcessingAction('');
    }
  };

  // Gérer les changements dans le formulaire d'email
  const handleEmailDataChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({ ...prev, [name]: value }));
  };

  // Gérer l'export Word
  const handleExportWord = async () => {
    setLoading(true);
    setProcessingAction('export');
    try {
      const docChildren = [];

      // 1. Résumé (s'il existe)
      if (summary && summary.text) {
        docChildren.push(
          new Paragraph({
            text: 'Résumé',
            heading: HeadingLevel.HEADING_1,
          })
        );
        // Conversion markdown -> HTML -> Paragraphs stylés
        const html = await markdownToHtml(summary.text);
        const summaryParagraphs = htmlToDocxParagraphs(html);
        docChildren.push(...summaryParagraphs);
        docChildren.push(new Paragraph(''));
      }

      // 2. Transcription
      if (transcription) {
        docChildren.push(
          new Paragraph({
            text: 'Transcription complète',
            heading: HeadingLevel.HEADING_1,
          })
        );
        if (transcription.utterances && Array.isArray(transcription.utterances)) {
          transcription.utterances.forEach(u => {
            const speaker = u.speaker || 'Locuteur';
            const text = u.text || '';
            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({ text: speaker + ' : ', bold: true }),
                  new TextRun(text),
                ],
              })
            );
          });
        } else if (transcription.fullText) {
          transcription.fullText.split(/\n+/).forEach(line => {
            if (line.trim()) {
              docChildren.push(new Paragraph(line));
            }
          });
        } else {
          docChildren.push(new Paragraph('Aucune transcription disponible.'));
        }
      }

      // Création et téléchargement du fichier Word
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: docChildren,
          },
        ],
      });
      const blob = await Packer.toBlob(doc);
      const fileName = `Reunion_${meeting.title || meeting.id}.docx`;
      saveAs(blob, fileName);
      await addExportNotification(fileName, 'Word (réunion complète)');
    } catch (error) {
      setError('Erreur lors de la génération du document Word');

      await addActionErrorNotification('l\'export du document Word', error.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
      setProcessingAction('');
    }
  };

  // Exporter seulement le résumé en Word
  const handleExportSummaryWord = async () => {
    if (!summary || !summary.text) {
      await addActionErrorNotification('l\'export du résumé', 'Aucun résumé disponible pour cette réunion');
      return;
    }

    setLoading(true);
    setProcessingAction('export');
    try {
      // Fonction pour formater la date au format jjMMAAA
      const formatDateForFilename = (date) => {
        if (!date) return 'SansDate';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'SansDate';
        
        const day = d.getDate().toString().padStart(2, '0');
        const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        
        return `${day}${month}${year}`;
      };

      // Nettoyer le titre pour le nom de fichier
      const cleanTitle = (meeting.title || 'Sans-titre')
        .replace(/[<>:"/\\|?*]/g, '-')
        .replace(/\s+/g, '-')
        .substring(0, 50);

      const doc = await createSummaryWordDocument(meeting, summary);
      const blob = await Packer.toBlob(doc);
      const dateFormatted = formatDateForFilename(meeting.date);
      const fileName = `${dateFormatted}-Réunion-${cleanTitle}-Résumé.docx`;
      saveAs(blob, fileName);
      await addExportNotification(fileName, 'Word');
    } catch (error) {
      await addActionErrorNotification('l\'export du résumé en Word', error.message || 'Erreur inconnue');

    } finally {
      setLoading(false);
      setProcessingAction('');
    }
  };

  // Exporter seulement la transcription en Word
  const handleExportTranscriptWord = async () => {
    if (!transcription) {
      await addActionErrorNotification('l\'export de la transcription', 'Aucune transcription disponible pour cette réunion');
      return;
    }

    setLoading(true);
    setProcessingAction('export');
    try {
      // Fonction pour formater la date au format jjMMAAA
      const formatDateForFilename = (date) => {
        if (!date) return 'SansDate';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'SansDate';
        
        const day = d.getDate().toString().padStart(2, '0');
        const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        
        return `${day}${month}${year}`;
      };

      // Nettoyer le titre pour le nom de fichier
      const cleanTitle = (meeting.title || 'Sans-titre')
        .replace(/[<>:"/\\|?*]/g, '-')
        .replace(/\s+/g, '-')
        .substring(0, 50);

      const doc = await createTranscriptWordDocument(meeting, transcription);
      const blob = await Packer.toBlob(doc);
      const dateFormatted = formatDateForFilename(meeting.date);
      const fileName = `${dateFormatted}-Réunion-${cleanTitle}-Transcription.docx`;
      saveAs(blob, fileName);
      await addExportNotification(fileName, 'Word');
    } catch (error) {
      await addActionErrorNotification('l\'export de la transcription en Word', error.message || 'Erreur inconnue');

    } finally {
      setLoading(false);
      setProcessingAction('');
    }
  };

  // Gérer les changements dans le formulaire de génération de résumé
  const handleSummaryDataChange = (e) => {
    const { name, value } = e.target;
    setGenerateSummaryData(prev => ({ ...prev, [name]: value }));
  };

  // Gérer la génération ou régénération de résumé
  const handleGenerateSummary = async () => {
    // Nettoyage du cache audio pour libérer la mémoire
    globalSpeakerSamplesCache.current = {};
    if (!meeting.transcriptionId) {
      await addActionErrorNotification('la génération du résumé', 'Transcription requise pour générer un résumé');
      return;
    }
    if (!selectedModel) {
      try {
        await addNotification({
          message: '⚠️ Veuillez sélectionner un modèle de résumé',
          type: 'error',
          category: 'summary',
          metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
        });
      } catch (notifError) {

      }
      return;
    }

    if (meeting.projectId) {
      setPendingSummaryGeneration({
        model: generateSummaryData.model || defaultSummaryModel,
        prompt: selectedModel.prompt,
        userMessage: generateSummaryData.userMessage,
        promptModelId: selectedModel.id
      });
      setGenerateSummaryDialogOpen(false);
      setSummaryStep(0);
      setProjectMeetingsDialogOpen(true);
      return;
    }

    setGenerateSummaryDialogOpen(false);
    setSummaryStep(0);
    await proceedWithSummaryGeneration([]);
  };

  const handleProjectMeetingsSelected = (selectedMeetingIds) => {
    setSelectedPreviousMeetingIds(selectedMeetingIds);
    proceedWithSummaryGeneration(selectedMeetingIds);
  };

  const handleProjectMeetingsDialogClose = () => {
    setProjectMeetingsDialogOpen(false);
    setPendingSummaryGeneration(null);
  };

  const proceedWithSummaryGeneration = async (selectedPreviousMeetingIds) => {
    setProjectMeetingsDialogOpen(false);
    setSelectedModel(null);
    setTabValue(1);
    setLoading(true);
    setProcessingAction('summary');

    try {
      let progressMessage = `📝 Génération du résumé en cours pour "${meeting.title}"`;

      if (selectedPreviousMeetingIds && selectedPreviousMeetingIds.length > 0) {
        progressMessage += ` (incluant ${selectedPreviousMeetingIds.length} réunion(s) antérieure(s) du projet)`;
      }

      progressMessage += '...';

      await addNotification({
        message: progressMessage,
        type: 'progress',
        category: 'summary',
        metadata: {
          meetingTitle: meeting.title,
          meetingId: meeting.id,
          isProgress: true,
          projectContextIncluded: selectedPreviousMeetingIds?.length > 0
        }
      });
    } catch (notifError) {

    }

    try {
      // Déterminer si c'est une régénération (résumé existant avec ID valide) ou nouvelle génération
      const isRegeneration = !!(summary && summary.id);
      const summaryId = isRegeneration ? summary.id : undefined;

      // Si c'est une régénération, mettre à jour le statut du résumé existant
      if (isRegeneration) {
        try {
          const summaryRef = doc(db, 'summaries', summaryId);
          await updateDoc(summaryRef, {
            text: deleteField(),
            error: deleteField(), // Supprimer l'erreur précédente s'il y en avait une
            errorDetails: deleteField(), // Supprimer les détails d'erreur
            status: 'En cours',
            updatedAt: serverTimestamp(),
          });

          // Mettre à jour l'état local immédiatement pour afficher "En cours"
          setSummary({
            ...summary,
            status: 'En cours',
            text: undefined,
            error: undefined,
            errorDetails: undefined
          });
        } catch (updateError) {

          // On continue même en cas d'erreur ici, le service en créera un nouveau au besoin
        }
      }
      
      const summaryOptions = pendingSummaryGeneration || {
        model: generateSummaryData.model || defaultSummaryModel,
        prompt: selectedModel?.prompt,
        userMessage: generateSummaryData.userMessage,
        promptModelId: selectedModel?.id
      };

      const newSummary = await summaryService.generateSummary(
        meeting.transcriptionId,
        {
          ...summaryOptions,
          summaryId: summaryId,
          selectedPreviousMeetingIds: selectedPreviousMeetingIds
        }
      );

      setPendingSummaryGeneration(null);

      // Vérifier que l'objet résumé contient bien un identifiant
      if (!newSummary) {

        throw new Error("Échec de la génération du résumé");
      }
      
      if (!newSummary.id) {

        // Si on a un ID existant et pas de nouvel ID, utiliser l'ancien
        if (summaryId) {
          newSummary.id = summaryId;
        } else {
          throw new Error("Le résumé généré n'a pas d'identifiant");
        }
      }
      
      // Mettre à jour l'état
      setSummary(newSummary);
      
      // Notification de succès créée automatiquement côté backend
    } catch (error) {
      // Utiliser le message d'erreur amélioré de summaryService s'il existe
      let errorMessage = error.message || 'Erreur lors de la génération du résumé';
      const isTimeout = error.isTimeout || false;

      // Fallback pour les erreurs HTTP non traitées par summaryService
      if (!error.message && error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 403) {
          errorMessage = 'Erreur d\'authentification. Veuillez vous reconnecter.';
        } else if (error.response.status === 500) {
          errorMessage = 'Erreur serveur lors de la génération du résumé';
        }
      }

      // Supprimer la notification de progression
      await removeProgressNotifications(meeting.id, 'summary');

      // Créer une notification d'erreur pour l'utilisateur
      try {
        await addNotification({
          message: isTimeout
            ? `⏱️ Timeout: ${errorMessage}`
            : `❌ ${errorMessage}`,
          type: 'error',
          category: 'summary',
          metadata: {
            meetingTitle: meeting.title,
            meetingId: meeting.id,
            error: error.message,
            isTimeout: isTimeout
          }
        });
      } catch (notifError) {
        console.error('Erreur lors de la création de la notification d\'erreur:', notifError);
      }

      // Logger l'erreur dans les logs système pour l'administration
      try {
        await systemLogService.logError(
          currentUser?.uid,
          currentUser?.email,
          currentUser?.displayName || 'Utilisateur',
          isTimeout
            ? `Timeout génération de résumé pour "${meeting.title}"`
            : `Échec de génération de résumé pour "${meeting.title}"`,
          error,
          {
            meetingId: meeting.id,
            meetingTitle: meeting.title,
            transcriptionId: meeting.transcriptionId,
            model: generateSummaryData?.model || 'unknown',
            errorType: isTimeout ? 'Timeout' : (error.response?.status ? `HTTP ${error.response.status}` : 'Unknown'),
            errorMessage: errorMessage,
            isTimeout: isTimeout
          }
        );
      } catch (logError) {
        console.error('Erreur lors du logging système:', logError);
      }
    } finally {
      setLoading(false);
      setProcessingAction('');
    }
  };

  // Fonction pour configurer un écouteur sur le résumé de la transcription
  function setupSummaryListener(transcriptionId) {
    // Éviter de reconfigurer si déjà en place
    if (unsubscribeSummary && summary?.transcriptionId === transcriptionId) {
      return;
    }
    
    // Nettoyer l'écouteur précédent si existant
    if (unsubscribeSummary) {
      unsubscribeSummary();
    }
    
    // Déterminer si l'utilisateur a accès à cette réunion
    const isOwner = meeting?.userId === currentUser?.uid;
    const isShared = Array.isArray(meeting?.sharedWith) && meeting.sharedWith.includes(currentUser?.uid);
    const isAdmin = currentUser?.profile?.isAdmin || currentUser?.isAdmin;
    const hasAccess = isOwner || isShared || isAdmin;
    
    // Configurer un nouvel écouteur en temps réel
    // Si l'utilisateur a accès à la réunion, permettre l'accès au résumé même s'il n'en est pas le créateur
    const unsubscribe = summaryService.onSummaryByTranscriptionId(transcriptionId, (summaryData) => {
      setSummary(prev => {
        // Éviter les mises à jour inutiles
        if (prev && summaryData && JSON.stringify(prev) === JSON.stringify(summaryData)) {
          return prev;
        }
        return summaryData;
      });
    }, { isAdmin: hasAccess }); // Utiliser hasAccess pour bypasser le filtre utilisateur
    
    // Sauvegarder la fonction de désabonnement
    setUnsubscribeSummary(() => unsubscribe);
  }

  // Fonctions de gestion de la suppression
  const handleDeleteMeeting = () => {
    setConfirmDeleteOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await meetingService.deleteMeeting(id);
      
      await addDeleteNotification(meeting.title, 'Réunion');
      
      // Rediriger vers la liste des réunions
      navigate('/meetings');
    } catch (error) {

      await addActionErrorNotification('la suppression de la réunion', error.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <style>{`
        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
        }
        .ProseMirror th, .ProseMirror td {
          border: 1px solid #ccc;
          padding: 4px 8px;
        }
        .ProseMirror th {
          background: #f5f5f5;
          font-weight: bold;
        }
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.05);
            opacity: 1;
          }
        }
      `}</style>
      
      {/* Hero Section premium avec informations de la réunion */}
      <MeetingHeroSection>
        {/* Élément décoratif flottant */}
        <MeetingFloatingDecoration />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* Header avec bouton retour et actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
            <AnimatedBackButton 
              aria-label="retour" 
              onClick={() => navigate('/meetings')}
            >
              <ArrowBackIcon />
            </AnimatedBackButton>
            
            {/* Titre centré */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                textShadow: '0 2px 6px rgba(0,0,0,0.3)',
                letterSpacing: '-0.01em',
                color: 'white',
                textAlign: 'center',
                flex: 1,
                mx: 2
              }}
            >
              {meeting.title}
            </Typography>
            
            <IconButton 
              aria-label="actions" 
              onClick={handleOpenMenu}
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '12px',
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.25)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Container>
        
        {/* Menu d'actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{ 
            elevation: 8, 
            sx: { 
              minWidth: 220, 
              mt: 1,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)'
            } 
          }}
        >
          <MenuItem onClick={() => {
            setEditDialogOpen(true);
            handleCloseMenu();
          }}>
            <EditIcon fontSize="small" sx={{ mr: 2 }} />
            Modifier la réunion
          </MenuItem>
          <MenuItem onClick={handleOpenShareDialog}>
            <ShareIcon fontSize="small" sx={{ mr: 2 }} />
            Partager
          </MenuItem>

          <Divider />
          <MenuItem onClick={handleDeleteMeeting} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
            Supprimer
          </MenuItem>
        </Menu>
      </MeetingHeroSection>
      
      {/* Section des statistiques */}
      <StatsSection>
        <Grid container spacing={2} justifyContent="center">
          {/* Bloc Date */}
          <Grid item xs={6} sm={3}>
            <MeetingStatCard color="#FF6B6B">
              <Box 
                className="stat-icon"
                sx={{ 
                  animation: 'floatIcon 3s ease-in-out infinite',
                  animationDelay: '0s'
                }}
              >
                <CalendarTodayIcon />
              </Box>
              <CardContent className="stat-content">
                <Typography className="stat-label">
                  Date
                </Typography>
                <Typography className="stat-value">
                  {formattedDate}
                </Typography>
              </CardContent>
            </MeetingStatCard>
          </Grid>
          
          {/* Bloc Durée avec animation count-up */}
          <Grid item xs={6} sm={3}>
            <DurationCard durationSeconds={durationSeconds} />
          </Grid>
          
          {/* Bloc Participants avec accordion */}
          <Grid item xs={6} sm={3}>
            <ParticipantsCard participants={meeting.participants} />
          </Grid>
          
          {/* Bloc Statut amélioré */}
          <Grid item xs={6} sm={3}>
            <StatusCard 
                                  isTranscribed={transcription?.validationStatus === 'completed' || (transcription?.status === 'completed' && !transcription?.validationStatus)}
              isSummarized={summary?.status === 'Terminé'}
              isMobile={isMobile}
            />
          </Grid>
        </Grid>
      </StatsSection>
      
      {/* Contenu principal */}
      <Container maxWidth="lg" sx={{ 
        flex: 1, 
        py: { xs: 2, sm: 3 }, 
        mb: { xs: 10, sm: 0 }, 
        px: { xs: 0, sm: 3 },
        position: 'relative',
        zIndex: 1 // Z-index plus bas que StatsSection pour permettre l'affichage de la liste des participants
      }}>
        {/* Si la réunion a une transcription, afficher le contenu */}
        {meeting.transcriptionId && transcription ? (
          <Box sx={{ position: 'relative' }}>
            {/* Navigation Desktop */}
            {!isMobile && (
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 3, 
                  mb: 3,
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden'
                }}
              >
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="meeting content tabs"
                  variant="fullWidth"
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 64,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      gap: 1,
                      '&.Mui-selected': {
                        color: 'primary.main',
                      }
                    },
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                    }
                  }}
                >
                  <Tab 
                    icon={<RecordVoiceOverIcon />} 
                    label="Transcription" 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<ArticleIcon />} 
                    label="Résumé" 
                    iconPosition="start"
                  />
                </Tabs>
              </Paper>
            )}
            
            {/* Navigation Mobile Bottom */}
            {isMobile && (
              <BottomNavigation
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                showLabels
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  borderTop: '1px solid #e0e0e0',
                  bgcolor: 'background.paper',
                  '& .MuiBottomNavigationAction-root': {
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: 'primary.main',
                    }
                  }
                }}
              >
                <BottomNavigationAction 
                  label="Transcription" 
                  icon={<RecordVoiceOverIcon />} 
                />
                <BottomNavigationAction 
                  label="Résumé" 
                  icon={<ArticleIcon />} 
                />
              </BottomNavigation>
            )}
          
            {/* Onglet Transcription */}
            <TabPanel value={tabValue} index={0}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 3, 
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Header avec actions */}
                <Box 
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <RecordVoiceOverIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Transcription 
                    </Typography>
                    {transcription.utterances && (
                      <Chip 
                        label={`${transcription.utterances.length} segments`}
                        size="small"
                        sx={{ bgcolor: 'primary.light', color: 'white' }}
                      />
                    )}
                  </Box>
                  
                  <IconButton 
                    aria-label="actions-transcription" 
                    onClick={e => setAnchorElTranscription(e.currentTarget)} 
                                            disabled={!(transcription?.validationStatus === 'completed' || (transcription?.status === 'completed' && !transcription?.validationStatus))}
                    sx={{ 
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  
                  <Menu
                    anchorEl={anchorElTranscription}
                    open={Boolean(anchorElTranscription)}
                    onClose={() => setAnchorElTranscription(null)}
                    PaperProps={{ 
                      elevation: 8, 
                      sx: { 
                        minWidth: 220, 
                        mt: 1,
                        borderRadius: 2
                      } 
                    }}
                  >
                    <MenuItem onClick={handleExportTranscriptWord}>
                      <DownloadIcon fontSize="small" sx={{ mr: 2 }} />
                      Exporter en Word
                    </MenuItem>
                    <MenuItem onClick={handleOpenTranscriptEmailDialog}>
                      <EmailIcon fontSize="small" sx={{ mr: 2 }} />
                      Envoyer par email
                    </MenuItem>
                    <MenuItem onClick={async () => {
                      if (!transcription) return;
                      let fullText = '';
                      // Fonction pour formater le temps en MM:SS
                      const formatTime = (seconds) => {
                        const minutes = Math.floor(seconds / 60);
                        const remainingSeconds = Math.floor(seconds % 60);
                        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
                      };
                      
                      if (transcription.utterances && Array.isArray(transcription.utterances)) {
                        fullText = transcription.utterances.map(u => {
                          const speaker = u.speaker ? u.speaker + ': ' : '';
                          const startTime = Math.floor(u.start / 1000);
                          const endTime = Math.floor(u.end / 1000);
                          return `[${formatTime(startTime)} - ${formatTime(endTime)}] ${speaker}${u.text}`;
                        }).join('\n\n');
                      }
                      try {
                        await navigator.clipboard.writeText(fullText);
                        await addCopyNotification('Transcription');
                      } catch (e) {
                        await addActionErrorNotification('la copie de la transcription', e.message || 'Erreur inconnue');
                      }
                      setAnchorElTranscription(null);
                    }}>
                      <ContentCopyIcon fontSize="small" sx={{ mr: 2 }} />
                      Copier la transcription
                    </MenuItem>
                  </Menu>
                </Box>
                
                {/* Contenu de la transcription */}
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {/* Affichage du coût de transcription */}
                                      {(transcription.validationStatus === 'completed' || (transcription.status === 'completed' && !transcription.validationStatus)) && durationSeconds && (
                    <Box sx={{ mb: 3 }}>
                      <CostDisplay
                        title="Coût de transcription"
                        cost={calculateTranscriptionCost(durationSeconds)}
                        icon={<MicIcon fontSize="large" />}
                        color="primary.main"
                        variant="outlined"
                        details={`AssemblyAI • Durée: ${formatDuration(durationSeconds)} • Tarif: $0.27/heure`}
                      />
                    </Box>
                  )}
                  
                  {transcription.utterances && transcription.utterances.length > 0 ? (
                    <VirtualizedTranscription
                      utterances={transcription.utterances}
                      speakerMapping={speakerMapping}
                      meeting={meeting}
                      onEditSpeaker={handleEditSpeaker}
                      containerRef={transcriptionContainerRef}
                      isMobile={isMobile}
                    />
                  ) : (transcription.validationStatus === 'completed' || (transcription.status === 'completed' && !transcription.validationStatus)) && transcription.fullText ? (
                    <Box sx={{ 
                      whiteSpace: 'pre-line', 
                      fontSize: '1rem', 
                      lineHeight: 1.6,
                      bgcolor: 'grey.50',
                      p: 3,
                      borderRadius: 2
                    }}>
                      {transcription.fullText}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Alert 
                        severity="info" 
                        variant="outlined"
                        icon={<CircularProgress size={24} color="info" />}
                        sx={{ 
                          mb: 3, 
                          borderRadius: 3,
                          border: 'none',
                          bgcolor: 'rgba(33, 150, 243, 0.08)'
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          Transcription en cours
                        </Typography>
                        <Typography variant="body1">
                          Votre fichier audio est en cours d'analyse.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Cette opération peut prendre quelques minutes.
                        </Typography>
                      </Alert>
                      
                      {meeting.status === 'transcribing' && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={async () => {
                            if (window.confirm('Êtes-vous sûr de vouloir annuler la transcription ?')) {
                              setLoading(true);
                              try {
                                await transcriptionService.cancelTranscription(meeting.id);
                                setMeeting({ ...meeting, status: 'pending' });
                                
                                // Supprimer les notifications de progression
                                await removeProgressNotifications(meeting.id, 'transcription');
                                
                                // Ajouter une notification d'annulation
                                await addCancelNotification('Transcription', meeting.title);
                              } catch (error) {

                                await addActionErrorNotification('l\'annulation de la transcription', error.message || 'Erreur inconnue');
                              } finally {
                                setLoading(false);
                              }
                            }
                          }}
                          sx={{ borderRadius: 3 }}
                        >
                          Annuler
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </TabPanel>
          
          {/* Onglet Résumé (toujours visible) */}
            <TabPanel value={tabValue} index={1}>
              <Paper sx={{ p: 3, position: 'relative' }}>
                {/* Menu d'actions pour le résumé */}
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
                  <IconButton aria-label="actions-summary" onClick={e => setAnchorElSummary(e.currentTarget)} 
                    disabled={!summary || summary.status !== 'Terminé'}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorElSummary}
                    open={Boolean(anchorElSummary)}
                    onClose={() => setAnchorElSummary(null)}
                    PaperProps={{ elevation: 2, sx: { minWidth: 200, mt: 1 } }}
                  >
                    <MenuItem onClick={handleExportSummaryWord}>
                      <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
                      Exporter résumé Word
                    </MenuItem>
                    <MenuItem onClick={handleOpenEmailDialog}>
                      <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                      Envoyer par email
                    </MenuItem>
                    <MenuItem onClick={async () => {
                      if (!summary || !summary.text) return;
                      try {
                        await navigator.clipboard.writeText(summary.text);
                        await addCopyNotification('Résumé');
                      } catch (e) {
                        await addActionErrorNotification('la copie du résumé', e.message || 'Erreur inconnue');
                      }
                      setAnchorElSummary(null);
                    }}>
                      <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
                      Copier le résumé
                    </MenuItem>
                  </Menu>
                </Box>
              {loading && processingAction === 'summary' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography>Génération du résumé en cours.....</Typography>
                </Box>
              ) : summary ? (
                summary.status === 'En cours' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography>Génération du résumé en cours.....</Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      sx={{ mt: 2 }}
                      onClick={async () => {
                        setLoading(true);
                        setProcessingAction('cancel-summary');
                        try {
                          await summaryService.cancelSummary(summary.id);
                          setSummary({ ...summary, status: 'cancelled' });
                          
                          // Supprimer les notifications de progression de résumé
                          try {
                            await removeProgressNotifications(meeting.id, 'summary');
                          } catch (notifError) {

                          }
                          
                          // Ajouter une notification d'annulation (jaunâtre)
                          try {
                            await addNotification({
                              message: `⚠️ Génération du résumé annulée pour "${meeting.title}"`,
                              type: 'warning',
                              category: 'summary',
                              metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
                            });
                          } catch (notifError) {

                          }
                          
                          await addCancelNotification('Génération du résumé', meeting.title);
                        } catch (e) {
                          // Ajouter une notification d'erreur d'annulation
                          try {
                            await addNotification({
                              message: `❌ Erreur lors de l'annulation du résumé pour "${meeting.title}": ${e.message || 'Erreur inconnue'}`,
                              type: 'error',
                              category: 'summary',
                              metadata: { meetingTitle: meeting.title, meetingId: meeting.id, errorMessage: e.message }
                            });
                          } catch (notifError) {

                          }
                          
                          await addActionErrorNotification('l\'annulation du résumé', e.message || 'Erreur inconnue');
                        } finally {
                          setLoading(false);
                          setProcessingAction('');
                        }
                      }}
                      disabled={loading && processingAction === 'cancel-summary'}
                    >
                      Annuler la génération
                    </Button>
                  </Box>
                ) : summary.status === 'cancelled' ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ mb: 2 }} color="warning.main">
                      La génération du résumé a été annulée.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DescriptionIcon />}
                      onClick={handleOpenSummaryDialog}
                      disabled={meeting.status === 'transcribing'}
                      title={meeting.status === 'transcribing' ? "La génération de résumé sera disponible une fois la transcription terminée" : ""}
                    >
                      Générer un résumé
                    </Button>
                  </Box>
                ) : summary.status === 'Erreur' ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }} color="error.main">
                      ❌ Erreur lors de la génération du résumé
                    </Typography>
                    {summary.error && (
                      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                        {summary.error}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<RefreshIcon />}
                      onClick={handleOpenSummaryDialog}
                      disabled={meeting.status === 'transcribing'}
                      title={meeting.status === 'transcribing' ? "La génération de résumé sera disponible une fois la transcription terminée" : ""}
                    >
                      Réessayer
                    </Button>
                  </Box>
                ) : summary.status === 'Terminé' ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      {/* Informations sur la génération */}
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                        Généré le {
                          summary.createdAt
                            ? (typeof summary.createdAt.toDate === 'function'
                                ? format(summary.createdAt.toDate(), "d MMMM yyyy 'à' HH:mm", { locale: fr })
                                : (isValid(new Date(summary.createdAt))
                          ? format(new Date(summary.createdAt), "d MMMM yyyy 'à' HH:mm", { locale: fr })
                                    : "date inconnue"))
                          : "date inconnue"
                      } avec {summary.model}
                      </Typography>
                      
                      {summary.promptModelId && (
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                          Modèle utilisé : {models.find(m => m.id === summary.promptModelId)?.title || summary.promptModelId}
                        </Typography>
                      )}
                      
                      {/* Affichage moderne du coût */}
                      {summary.usage && summary.usage.cost && (
                        <CostDisplay
                          title="Coût de génération du résumé"
                          cost={summary.usage.cost}
                          tokens={summary.usage.tokens}
                          icon={<AutoAwesomeIcon fontSize="large" />}
                          color="secondary.main"
                          variant="outlined"
                        />
                      )}

                      {/* Avertissement de troncature si applicable */}
                      {summary.truncationInfo && summary.truncationInfo.wasTruncated && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <AlertTitle>Résumé généré avec transcription partielle</AlertTitle>
                          ⚠️ En raison des limites de l'API OpenAI (Tier 1: 30,000 tokens/minute),
                          seulement <strong>{summary.truncationInfo.percentageKept}%</strong> de la transcription
                          a été utilisée pour générer ce résumé.
                        </Alert>
                      )}
                    </Box>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      {summary && summary.status === 'Terminé' && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={!isMobile && <RefreshIcon />}
                          onClick={handleOpenSummaryDialog}
                          sx={{
                            [theme.breakpoints.down('sm')]: {
                              minWidth: '56px',
                              width: '56px',
                              height: '56px',
                              borderRadius: '50%',
                              p: 0,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              display: 'inline-flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }
                          }}
                        >
                          {isMobile ? <RefreshIcon /> : 'Regénérer le résumé'}
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ minHeight: 200 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary.text}</ReactMarkdown>
                    </Box>
                  </>
                ) : null
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  {/* Statistiques de la transcription */}
                  {transcription?.utterances && Array.isArray(transcription.utterances) && transcription.utterances.length > 0 && (
                    (() => {
                      // Fonction de formatage identique à la fonction Cloud
                      const formatTime = (seconds) => {
                        const minutes = Math.floor(seconds / 60);
                        const remainingSeconds = Math.floor(seconds % 60);
                        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
                      };
                      const formattedText = transcription.utterances.map(u => {
                        const startTime = Math.floor(u.start / 1000);
                        const endTime = Math.floor(u.end / 1000);
                        return `[${formatTime(startTime)} - ${formatTime(endTime)}] ${u.speaker}: ${u.text}`;
                      }).join('\n\n');
                      // Calcul simplifié du nombre de tokens (approximation)
                      const charCount = formattedText.length;
                      // Estimation: environ 1 token pour 4 caractères en moyenne
                      const tokenCount = Math.round(charCount / 4);
                      const segmentCount = transcription.utterances.length;
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Statistiques de la transcription :
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {segmentCount} segment{segmentCount > 1 ? 's' : ''} – {charCount} caractères – env. {tokenCount} tokens
                          </Typography>
                        </Box>
                      );
                    })()
                  )}
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Aucun résumé n'est disponible pour cette réunion.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DescriptionIcon />}
                    onClick={handleOpenSummaryDialog}
                    disabled={meeting.status === 'transcribing'}
                    title={meeting.status === 'transcribing' ? "La génération de résumé sera disponible une fois la transcription terminée" : ""}
                  >
                    Générer un résumé
                  </Button>
                  {meeting.status === 'transcribing' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      La génération de résumé sera disponible une fois la transcription terminée
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </TabPanel>
          </Box>
        ) : meeting.transcriptionId && !transcription ? (
          // Si la réunion a un transcriptionId mais que la transcription n'est pas encore chargée
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e0e0e0'
            }}
          >
            <Box>
              <CircularProgress size={64} sx={{ mb: 3, color: 'primary.main' }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Chargement de la transcription
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary">
                Récupération des données de transcription en cours...
              </Typography>
            </Box>
          </Paper>
        ) : (
          // Si la réunion n'a pas de transcription
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e0e0e0'
            }}
          >
            {meeting.status === 'pending' ? (
              <Box>
                <RecordVoiceOverIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Prêt pour la transcription
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  La transcription n'a pas encore été lancée. Le processus utilise l'IA pour identifier automatiquement les locuteurs.
                </Typography>
                
                <Alert 
                  severity="info" 
                  sx={{ 
                    maxWidth: 500, 
                    mx: 'auto', 
                    mb: 3, 
                    textAlign: 'left',
                    borderRadius: 2,
                    border: 'none'
                  }}
                >
                  <Typography variant="body2">
                    <strong>Information:</strong> Le fichier audio sera traité par AssemblyAI pour une transcription précise avec identification des locuteurs.
                  </Typography>
                </Alert>
                
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={async () => {
                    if (!Array.isArray(meeting.participants) || meeting.participants.length === 0) {
                      try {
                        await addNotification({
                          message: '⚠️ Veuillez renseigner au moins un participant avant de démarrer la transcription',
                          type: 'error',
                          category: 'transcription',
                          metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
                        });
                      } catch (notifError) {

                      }
                      return;
                    }
                    setLoading(true);
                    setProcessingAction('transcription');
                    setError(null);
                    setTranscriptionError(null);
                    
                    // Créer la notification de progression côté client pour plus de réactivité
                    try {
                      await addNotification({
                        message: `🎤 Transcription en cours pour "${meeting.title}"...`,
                        type: 'progress',
                        category: 'transcription',
                        metadata: { 
                          meetingTitle: meeting.title, 
                          meetingId: meeting.id,
                          isProgress: true
                        }
                      });
                    } catch (notifError) {

                    }
                    
                    try {
                      const result = await transcriptionService.startTranscription(meeting.id, meeting.audioUrl);
                      if (result.success) {
                        setMeeting({ ...meeting, status: 'transcribing' });
                      } else {
                        // Supprimer la notification de progression en cas d'erreur
                        await removeProgressNotifications(meeting.id, 'transcription');
                        throw new Error(result.message || 'Erreur lors du démarrage');
                      }
                    } catch (error) {
                      setTranscriptionError('Erreur lors du démarrage de la transcription');
                      // Supprimer la notification de progression et laisser le backend créer la notification d'erreur
                      await removeProgressNotifications(meeting.id, 'transcription');
                    } finally {
                      setLoading(false);
                      setProcessingAction('');
                    }
                  }}
                  startIcon={loading && processingAction === 'transcription' ? <CircularProgress size={20} color="inherit" /> : <MicIcon />}
                  disabled={loading}
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    borderRadius: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  {loading && processingAction === 'transcription' ? 'Démarrage...' : 'Démarrer la transcription'}
                </Button>
                
                {transcriptionError && (
                  <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto', mt: 3, borderRadius: 2 }}>
                    {transcriptionError}
                  </Alert>
                )}
              </Box>
            ) : meeting.status === 'transcribing' ? (
              <Box>
                <CircularProgress size={64} sx={{ mb: 3, color: 'primary.main' }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Transcription en cours
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  L'IA analyse votre fichier audio. Cette opération peut prendre quelques minutes.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Vous pouvez quitter cette page, la transcription continuera en arrière-plan.
                </Typography>
                
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                    sx={{ borderRadius: 3 }}
                  >
                    Actualiser
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={async () => {
                      try {
                        setLoading(true);

                        // Forcer la remise à pending du meeting
                        await meetingService.updateMeeting(meeting.id, { 
                          status: 'pending',
                          updatedAt: new Date() 
                        });
                        setMeeting({ ...meeting, status: 'pending' });
                        await addNotification({
                          message: '🔄 Statut forcé à "pending" - vous pouvez maintenant relancer',
                          type: 'success',
                          category: 'transcription'
                        });
                      } catch (error) {

                        await addActionErrorNotification('la réinitialisation forcée', error.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    sx={{ borderRadius: 3 }}
                  >
                    🔧 Forcer reset
                  </Button>
                      
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={loading && processingAction === 'cancel' ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
                    onClick={async () => {
                      setLoading(true);
                      setProcessingAction('cancel');
                      try {
                        const result = await transcriptionService.cancelTranscription(meeting.id);
                        if (result.success) {
                          // Mettre à jour le statut de la réunion
                          setMeeting({ ...meeting, status: 'pending' });
                          
                          // Supprimer les notifications de progression
                          await removeProgressNotifications(meeting.id, 'transcription');
                          
                          // Ajouter une notification d'annulation
                          await addCancelNotification('Transcription', meeting.title);
                        } else {
                          // Gérer le cas où l'annulation échoue
                          throw new Error(result.message || 'Échec de l\'annulation de la transcription');
                        }
                      } catch (error) {

                        await addActionErrorNotification('l\'annulation de la transcription', error.message || 'Erreur inconnue');
                      } finally {
                        setLoading(false);
                        setProcessingAction('');
                      }
                    }}
                    disabled={loading}
                    sx={{ borderRadius: 3 }}
                  >
                    {loading && processingAction === 'cancel' ? 'Annulation...' : 'Annuler'}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box>
                <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom color="error" sx={{ fontWeight: 600 }}>
                  Erreur de transcription
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  Une erreur s'est produite lors du traitement de votre fichier audio.
                </Typography>
                
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                  <Button 
                    variant="outlined" 
                    color="error"
                    startIcon={loading && processingAction === 'reset' ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={async () => {
                      setLoading(true);
                      setProcessingAction('reset');
                      try {
                        await meetingService.updateMeeting(meeting.id, { ...meeting, status: 'pending' });
                        setMeeting({ ...meeting, status: 'pending' });
                        await addNotification({
                          message: '🔄 Statut de la transcription réinitialisé',
                          type: 'success',
                          category: 'transcription',
                          metadata: { meetingTitle: meeting.title, meetingId: meeting.id }
                        });
                      } catch (error) {
                        await addActionErrorNotification('la réinitialisation du statut', error.message || 'Erreur inconnue');
                      } finally {
                        setLoading(false);
                        setProcessingAction('');
                      }
                    }}
                    disabled={loading}
                    sx={{ borderRadius: 3 }}
                  >
                    {loading && processingAction === 'reset' ? 'Réinitialisation...' : 'Réessayer'}
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={() => navigate('/meetings')}
                    sx={{ borderRadius: 3 }}
                  >
                    Retour à la liste
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        )}
      </Container>
      
      {/* Scroll to top button */}
             {/* <Fab
         color="primary"
         size="medium"
         aria-label="scroll to top"
         onClick={() => {
           // Si on est dans l'onglet transcription et que le conteneur existe, scroller dans le conteneur
           if (tabValue === 0 && transcriptionContainerRef.current) {
             transcriptionContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
           } else {
             // Sinon scroller dans la fenêtre
             window.scrollTo({ top: 0, behavior: 'smooth' });
           }
         }}
         sx={{
           position: 'fixed',
           bottom: isMobile ? 72 : 32, // Juste au-dessus de la bottom nav
           right: 16, // Plus près du bord sur mobile
           zIndex: 999,
           boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
           '&:hover': {
             transform: 'scale(1.1)',
           },
           transition: 'all 0.2s ease'
         }}
       >
        <KeyboardArrowUpIcon />
      </Fab> */}
      
      {/* Boîte de dialogue de partage */}
      <BaseDialog
        open={shareDialogOpen}
        onClose={handleCloseShareDialog}
        title="🤝 Partager cette réunion"
        subtitle="Donnez accès à cette réunion à un collègue"
        maxWidth="sm"
        customBackgroundContent="#f8faff"
      >

        <DialogContent sx={{ 
          px: isMobile ? 2 : 4, 
          py: isMobile ? 2 : 4, 
          background: '#fafbff',
          overflow: 'auto',
          flex: 1
        }}>
          <Box sx={{ 
            background: 'white',
            borderRadius: '16px',
            p: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e3f2fd'
          }}>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                mb: 3,
                color: 'text.primary',
                fontSize: isMobile ? '14px' : '16px'
              }}
            >
              Sélectionnez un membre de l'organisation avec qui partager cette réunion.
            </Typography>
            <Autocomplete
              value={shareSelectedUser}
              onChange={(event, newValue) => setShareSelectedUser(newValue)}
              inputValue={shareUserInput}
              onInputChange={(event, newInputValue) => setShareUserInput(newInputValue)}
              options={shareUserOptions}
              getOptionLabel={option => option.displayName || option.email || ''}
              loading={shareLoading}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Utilisateur"
                  placeholder="Nom ou email..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#f8faff',
                      '& fieldset': {
                        borderColor: '#e1e8f0',
                        borderWidth: 2
                      },
                      '&:hover fieldset': {
                        borderColor: '#3F51B5'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3F51B5',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px' // Toujours 16px pour éviter le zoom automatique sur iOS
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '16px',
                      opacity: 0.7
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 600,
                      color: '#2c3e50',
                      fontSize: '16px' // Toujours 16px pour cohérence
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {shareLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {option.photoURL ? (
                      <Avatar src={option.photoURL} sx={{ width: 32, height: 32, mr: 1.5 }} />
                    ) : (
                      <Avatar sx={{ width: 32, height: 32, mr: 1.5 }}>{option.displayName?.charAt(0) || '?'}</Avatar>
                    )}
                    <Box>
                      <Typography variant="body1">{option.displayName}</Typography>
                      <Typography variant="body2" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </Box>
                </li>
              )}
              noOptionsText="Aucun utilisateur trouvé"
              loadingText="Recherche en cours..."
              fullWidth
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          px: isMobile ? 2 : 4, 
          py: 2, 
          background: 'white',
          borderTop: '1px solid #e3f2fd',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={handleCloseShareDialog}
            variant="outlined"
            sx={{ 
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              borderColor: '#e0e0e0',
              color: '#666',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#bdbdbd',
                background: '#f5f5f5'
              }
            }}
          >
            Annuler
          </Button>
          <Button 
            variant="contained" 
            onClick={handleShareMeeting}
            disabled={!shareSelectedUser || shareLoading}
            sx={{ 
              borderRadius: '12px',
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
              boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              '&:hover': {
                background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(63, 81, 181, 0.4)'
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e',
                boxShadow: 'none'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Partager
          </Button>
        </DialogActions>
      </BaseDialog>
      
      {/* Boîte de dialogue d'envoi d'email */}
      <BaseDialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        title="📧 Envoyer par email"
        subtitle="Partagez cette réunion par email"
        maxWidth="md"
        customBackgroundContent="#fafbff"
      >
        {/* En-tête avec dégradé */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)',
            py: isMobile ? 6.5 : 2.5,
            px: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.3
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography 
                variant="h5" 
                fontWeight={700}
                sx={{ 
                  color: 'white',
                  mb: 0.5,
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  fontSize: { xs: '1.1rem', sm: '1.5rem' }
                }}
              >
                📧 Envoyer par email
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  fontSize: { xs: '0.8rem', sm: '1rem' }
                }}
              >
                Partagez cette réunion par email
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseEmailDialog} 
              size="small"
              sx={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                p: 1.5,
                color: 'white',
                '&:hover': {
                  background: 'rgba(255,255,255,0.25)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ 
          px: isMobile ? 2 : 4, 
          py: isMobile ? 2 : 4, 
          background: '#fafbff',
          overflow: 'auto',
          flex: 1
        }}>
          <Box sx={{ 
            background: 'white',
            borderRadius: '16px',
            p: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e3f2fd'
          }}>
            <TextField
              autoFocus
              margin="dense"
              name="to"
              label="Destinataire"
              type="email"
              fullWidth
              variant="outlined"
              value={emailData.to}
              onChange={handleEmailDataChange}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f8faff',
                  '& fieldset': {
                    borderColor: '#e1e8f0',
                    borderWidth: 2
                  },
                  '&:hover fieldset': {
                    borderColor: '#3F51B5'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3F51B5',
                    borderWidth: 2
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px' // Toujours 16px pour éviter le zoom automatique sur iOS
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '16px',
                  opacity: 0.7
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '16px' // Toujours 16px pour cohérence
                }
              }}
            />
            
            <TextField
              margin="dense"
              name="subject"
              label="Sujet"
              fullWidth
              variant="outlined"
              value={emailData.subject}
              onChange={handleEmailDataChange}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f8faff',
                  '& fieldset': {
                    borderColor: '#e1e8f0',
                    borderWidth: 2
                  },
                  '&:hover fieldset': {
                    borderColor: '#3F51B5'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3F51B5',
                    borderWidth: 2
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px' // Toujours 16px pour éviter le zoom automatique sur iOS
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '16px',
                  opacity: 0.7
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '16px' // Toujours 16px pour cohérence
                }
              }}
            />
            
            <TextField
              margin="dense"
              name="message"
              label="Message"
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              value={emailData.message}
              onChange={handleEmailDataChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f8faff',
                  '& fieldset': {
                    borderColor: '#e1e8f0',
                    borderWidth: 2
                  },
                  '&:hover fieldset': {
                    borderColor: '#3F51B5'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3F51B5',
                    borderWidth: 2
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px' // Toujours 16px pour éviter le zoom automatique sur iOS
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '16px',
                  opacity: 0.7
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '16px' // Toujours 16px pour cohérence
                }
              }}
            />
            
            <Box sx={{ mt: 2 }}>
              <Typography 
                variant="body2" 
                color="textSecondary"
                sx={{ 
                  fontSize: isMobile ? '12px' : '14px',
                  fontStyle: 'italic'
                }}
              >
                La transcription et le résumé (si disponible) seront attachés au format Word.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          px: isMobile ? 2 : 4, 
          py: 2, 
          background: 'white',
          borderTop: '1px solid #e3f2fd',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={handleCloseEmailDialog}
            variant="outlined"
            sx={{ 
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              borderColor: '#e0e0e0',
              color: '#666',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#bdbdbd',
                background: '#f5f5f5'
              }
            }}
          >
            Annuler
          </Button>
          <Button 
            variant="contained"
            onClick={handleSendEmail}
            disabled={!emailData.to}
            sx={{ 
              borderRadius: '12px',
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
              boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              '&:hover': {
                background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(63, 81, 181, 0.4)'
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e',
                boxShadow: 'none'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Envoyer
          </Button>
        </DialogActions>
      </BaseDialog>
      
      {/* Dialog unifiée de génération de résumé avec étapes */}
      <BaseDialog
        open={generateSummaryDialogOpen}
        onClose={() => {
          setGenerateSummaryDialogOpen(false);
          setSummaryStep(0);
          setSelectedModel(null);
          setTempSpeakerMapping({ ...speakerMapping });
          stopAudio();
        }}
        title={summaryStep === 0 ? "🎤 Identification des locuteurs" : "✨ Générer un résumé"}
        subtitle={summaryStep === 0 ? "Associez chaque voix à un participant" : "Sélectionnez un modèle et personnalisez vos instructions"}
        maxWidth="lg"
        customBackgroundContent="#f8fafd"
        actions={
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
            <BaseButton 
              onClick={() => {
                if (summaryStep === 0) {
                  setGenerateSummaryDialogOpen(false);
                  setSummaryStep(0);
                  setSelectedModel(null);
                  setTempSpeakerMapping({ ...speakerMapping });
                  stopAudio();
                } else {
                  setSummaryStep(0);
                }
              }}
              variant="outlined"
                sx={{ 
                borderRadius: '16px',
                px: 4,
                py: 1.5,
                borderColor: 'rgba(79, 70, 229, 0.2)',
                color: '#64748b',
                fontWeight: 600,
                textTransform: 'none',
                backdropFilter: 'blur(8px)',
                background: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(79, 70, 229, 0.4)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {summaryStep === 0 ? 'Annuler' : 'Retour'}
            </BaseButton>
            
            {summaryStep === 0 ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <BaseButton 
                  onClick={() => {
                    setSummaryStep(1); // Passer à l'étape suivante sans appliquer les mappings
                  }}
                  variant="outlined"
                  sx={{
                    borderRadius: '16px',
                    px: 3,
                    py: 1.5,
                    borderColor: 'rgba(255, 152, 0, 0.3)',
                    color: '#FF9800',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'rgba(255, 152, 0, 0.5)',
                      background: 'rgba(255, 152, 0, 0.1)'
                    }
                  }}
                >
                  Passer cette étape
                </BaseButton>
                <BaseButton
                  onClick={async () => {
                    try {
                      // Appliquer les mappings
                      setSpeakerMapping(tempSpeakerMapping);
                      
                      if (transcription?.id) {
                        await transcriptionService.updateSpeakerMapping(transcription.id, tempSpeakerMapping);
                        
                        for (const speaker in tempSpeakerMapping) {
                          if (speaker.startsWith('custom_')) continue;
                          
                          const newValue = tempSpeakerMapping[speaker];
                          if (newValue && newValue !== 'autre' && newValue !== speaker) {
                            await transcriptionService.updateParticipantNameInUtterances(
                              transcription.id,
                              speaker,
                              newValue
                            );
                          }
                        }
                      }
                      
                      setSummaryStep(1); // Passer à l'étape suivante
                    } catch (e) {

                      await addActionErrorNotification('la sauvegarde des attributions de locuteurs', e.message || 'Erreur inconnue');
                    }
                  }}
            sx={{ 
                    borderRadius: '16px',
                    px: 4,
              py: 1.5,
                    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(79, 70, 229, 0.3)',
              textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: 'white',
              '&:hover': {
                      background: 'linear-gradient(135deg, rgba(79, 70, 229, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(79, 70, 229, 0.4)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Suivant
                </BaseButton>
              </Box>
            ) : (
              <BaseButton
            onClick={handleGenerateSummary}
            disabled={!selectedModel}
            sx={{ 
                  borderRadius: '16px',
                  px: 4,
                  py: 1.5,
                  background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 8px 32px rgba(79, 70, 229, 0.3)',
              textTransform: 'none',
              fontWeight: 600,
                  fontSize: '15px',
                  color: 'white',
              '&:hover': {
                    background: 'linear-gradient(135deg, rgba(79, 70, 229, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(79, 70, 229, 0.4)'
              },
              '&:disabled': {
                    background: 'rgba(148, 163, 184, 0.6)',
                    color: 'rgba(255, 255, 255, 0.7)',
                    boxShadow: 'none',
                    transform: 'none'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                ✨ Générer le résumé
              </BaseButton>
            )}
          </Box>
        }
      >
        {summaryStep === 0 ? (
          // Étape 1 : Identification des locuteurs
          <Box sx={{ 
            minHeight: '50vh',
            maxHeight: '70vh',
            overflow: 'auto',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(79, 70, 229, 0.2)',
              borderRadius: '10px',
                '&:hover': {
                background: 'rgba(79, 70, 229, 0.3)',
              },
            },
          }}>
        {/* Barre de chargement globale pour les extraits audio */}
        {audioPreloadProgress.total > 0 && audioPreloadProgress.loaded < audioPreloadProgress.total && (
              <Box sx={{ mb: 3, p: 3, background: 'rgba(255, 255, 255, 0.9)', borderRadius: '16px' }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#475569', fontWeight: 500 }}>
                  Préparation des extraits audio...
                </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.round((audioPreloadProgress.loaded / audioPreloadProgress.total) * 100)}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(79, 70, 229, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                      borderRadius: 3
                    }
                  }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center', color: '#64748b' }}>
                  {audioPreloadProgress.loaded} / {audioPreloadProgress.total}
            </Typography>
          </Box>
        )}
        
            {/* Description */}
          <Box sx={{ 
              mb: 4,
              p: 3,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              border: '1px solid rgba(79, 70, 229, 0.1)',
              boxShadow: '0 4px 20px rgba(79, 70, 229, 0.08)'
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                  color: '#475569',
                  fontSize: { xs: '14px', sm: '16px' },
                  lineHeight: 1.6,
                  textAlign: 'center'
                }}
              >
                🎯 Pour améliorer la qualité de votre résumé, identifiez chaque locuteur en écoutant un extrait et en sélectionnant le participant correspondant.
            </Typography>
            </Box>
            
            {detectedSpeakers.length > 0 ? (
              <Grid container spacing={3}>
                {detectedSpeakers.map((speaker) => {
                  const samplesObjs = globalSpeakerSamplesCache.current[speaker] || [];
                  return (
                    <Grid item xs={12} sm={6} md={4} key={speaker}>
                      <Box 
                        sx={{ 
                          p: 3,
                          height: '100%', 
                          background: tempSpeakerMapping[speaker] 
                            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(102, 187, 106, 0.15) 100%)'
                            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                          backdropFilter: 'blur(20px)',
                          borderRadius: '20px',
                          border: tempSpeakerMapping[speaker] 
                            ? '2px solid rgba(76, 175, 80, 0.4)'
                            : currentPlaying.speaker === speaker 
                            ? '2px solid rgba(79, 70, 229, 0.4)'
                            : '1px solid rgba(79, 70, 229, 0.1)',
                          boxShadow: tempSpeakerMapping[speaker]
                            ? '0 8px 32px rgba(76, 175, 80, 0.2)'
                            : '0 4px 20px rgba(79, 70, 229, 0.08)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: tempSpeakerMapping[speaker]
                              ? '0 12px 40px rgba(76, 175, 80, 0.3)'
                              : '0 8px 32px rgba(79, 70, 229, 0.15)',
                          },
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {/* En-tête avec avatar et nom */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: tempSpeakerMapping[speaker] ? '#4caf50' : '#4f46e5', 
                            width: 36, 
                            height: 36, 
                            fontSize: '1rem',
                            mr: 1.5
                          }}>
                              {speaker}
                            </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                              Locuteur {speaker}
                            </Typography>
                            {tempSpeakerMapping[speaker] && (
                              <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                ✓ {tempSpeakerMapping[speaker] === 'autre' 
                                  ? tempSpeakerMapping[`custom_${speaker}`] || 'Autre'
                                  : tempSpeakerMapping[speaker]
                                }
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Sélecteur de participant */}
                          <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                          <InputLabel>Participant</InputLabel>
                            <Select
                              value={tempSpeakerMapping[speaker] || ''}
                              onChange={(e) => handleTempMappingChange(speaker, e.target.value)}
                              label="Participant"
                              sx={{
                                  borderRadius: '12px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(79, 70, 229, 0.2)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(79, 70, 229, 0.4)'
                                }
                              }}
                            >
                              <MenuItem value={speaker}>Non identifié</MenuItem>
                              {meeting.participants?.map((participant, idx) => (
                              <MenuItem key={idx} value={participant.displayName}>
                                  {participant.displayName}
                                </MenuItem>
                              ))}
                              <MenuItem value="autre">Autre...</MenuItem>
                            </Select>
                          </FormControl>

                        {/* Champ nom personnalisé */}
                          {tempSpeakerMapping[speaker] === 'autre' && (
                            <TextField
                              label="Nom personnalisé"
                              value={tempSpeakerMapping[`custom_${speaker}`] || ''}
                              onChange={(e) => handleTempMappingChange(`custom_${speaker}`, e.target.value)}
                              fullWidth
                              variant="outlined"
                              size="small"
                              sx={{ 
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                borderRadius: '12px'
                                }
                              }}
                            />
                          )}

                        {/* Extraits audio */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', mb: 1, display: 'block' }}>
                            Extraits audio ({samplesObjs.length})
                          </Typography>
                          <Stack spacing={1}>
                            {samplesObjs.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                Aucun extrait disponible
                              </Typography>
                            ) : (
                              samplesObjs.map((obj, idx) => (
                                <BaseButton
                                  key={idx}
                                  variant={currentPlaying.speaker === speaker && currentPlaying.idx === idx ? 'contained' : 'outlined'}
                                  size="small"
                                  onClick={() => {
                                    if (currentPlaying.speaker === speaker && currentPlaying.idx === idx) {
                                      stopAudio();
                                    } else {
                                      playSpeakerSample(speaker, idx);
                                    }
                                  }}
                                  disabled={!obj.ready}
                                  sx={{ 
                                    fontSize: '11px',
                                    textTransform: 'none',
                                    justifyContent: 'flex-start',
                                    px: 2,
                                    py: 1
                                  }}
                                  startIcon={
                                    currentPlaying.speaker === speaker && currentPlaying.idx === idx 
                                      ? <PauseIcon sx={{ fontSize: 14 }} />
                                      : obj.ready 
                                      ? <VolumeUpIcon sx={{ fontSize: 14 }} />
                                      : <CircularProgress size={14} />
                                  }
                                >
                                  <Typography variant="caption" sx={{ 
                                    flex: 1, 
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis',
                                    maxWidth: '120px'
                                  }}>
                                    {obj.sample.text.slice(0, 30)}{obj.sample.text.length > 30 ? '...' : ''}
                                </Typography>
                                </BaseButton>
                              ))
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: '16px',
                  background: 'rgba(33, 150, 243, 0.08)',
                  border: '1px solid rgba(33, 150, 243, 0.2)'
                }}
              >
                Aucun locuteur n'a été détecté dans la transcription.
              </Alert>
            )}
          </Box>
        ) : (
          // Étape 2 : Sélection du modèle et configuration (on réutilise le contenu existant)
          <Box sx={{ 
            minHeight: '60vh',
            maxHeight: '75vh',
            overflow: 'auto',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(79, 70, 229, 0.2)',
              borderRadius: '10px',
              '&:hover': {
                background: 'rgba(79, 70, 229, 0.3)',
              },
            },
          }}>
            {/* Description */}
            {/* <Box sx={{ 
              mb: 4,
              p: 3,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              border: '1px solid rgba(79, 70, 229, 0.1)',
              boxShadow: '0 4px 20px rgba(79, 70, 229, 0.08)'
            }}>
              <Typography 
                variant="body1"
                sx={{ 
                  color: '#475569',
                  fontSize: { xs: '14px', sm: '16px' },
                  lineHeight: 1.6,
                  textAlign: 'center'
                }}
              >
                🎯 Choisissez un modèle de résumé adapté à vos besoins et ajoutez vos instructions personnalisées
              </Typography>
            </Box> */}

            {/* Section modèles et prompt */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
        sx={{
                  mb: 3,
                  fontWeight: 700,
                  color: '#334155',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                📋 Modèles de résumé
              </Typography>
              
              <Grid container spacing={3}>
                {/* Grille des modèles */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    {models.map((model) => (
                      <Grid item xs={12} sm={6} md={6} key={model.id}>
                        <Box
                          onClick={() => setSelectedModel(model)}
          sx={{
                            p: 2.5,
                            height: 'auto',
                            cursor: 'pointer',
                            background: selectedModel?.id === model.id 
                              ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)'
                              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '16px',
                            border: selectedModel?.id === model.id 
                              ? '2px solid rgba(79, 70, 229, 0.4)'
                              : '1px solid rgba(79, 70, 229, 0.1)',
                            boxShadow: selectedModel?.id === model.id
                              ? '0 6px 24px rgba(79, 70, 229, 0.2)'
                              : '0 3px 16px rgba(79, 70, 229, 0.08)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '160px',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
                              background: selectedModel?.id === model.id
                                ? 'linear-gradient(45deg, rgba(79, 70, 229, 0.05) 0%, transparent 50%, rgba(99, 102, 241, 0.05) 100%)'
                                : 'transparent',
                              zIndex: 0,
                            },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: selectedModel?.id === model.id
                                ? '0 8px 32px rgba(79, 70, 229, 0.3)'
                                : '0 6px 24px rgba(79, 70, 229, 0.15)',
                              border: selectedModel?.id === model.id 
                                ? '2px solid rgba(79, 70, 229, 0.6)'
                                : '1px solid rgba(79, 70, 229, 0.3)',
                            }
                          }}
                        >
                          {/* Indicateur de sélection */}
                          {selectedModel?.id === model.id && (
                            <Box sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2,
                              boxShadow: '0 2px 8px rgba(79, 70, 229, 0.4)'
                            }}>
                              <CheckIcon sx={{ fontSize: 12, color: 'white' }} />
                            </Box>
                          )}
                          
                          <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography 
                              variant="h6" 
                sx={{ 
                                fontWeight: 700,
                                color: selectedModel?.id === model.id ? '#4f46e5' : '#1e293b',
                                fontSize: { xs: '0.95rem', sm: '1rem' },
                                mb: 1,
                                lineHeight: 1.3,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {model.title}
                            </Typography>
                            
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#64748b',
                                fontSize: { xs: '12px', sm: '13px' },
                                lineHeight: 1.4,
                                mb: 2,
                                flex: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {model.description}
                            </Typography>
                            
                            {/* Zone distincte pour les mots-clés */}
                            {model.tags && model.tags.length > 0 && (
                              <Box sx={{ 
                                mt: 'auto',
                                p: 1.5,
                                background: selectedModel?.id === model.id 
                                  ? 'rgba(79, 70, 229, 0.08)' 
                                  : 'rgba(248, 250, 252, 0.8)',
                                borderRadius: '12px',
                                border: '1px solid rgba(79, 70, 229, 0.1)'
                              }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#64748b',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    mb: 1,
                                    display: 'block'
                                  }}
                                >
                                  🏷️ Mots-clés
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {model.tags.map((tag, idx) => (
                                    <Chip 
                                      key={idx} 
                                      label={tag} 
                                      size="small" 
                                      sx={{
                                        background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
                  color: 'white', 
                                        fontSize: '9px',
                                        height: 16,
                                        '& .MuiChip-label': {
                                          px: 0.5
                                        }
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Zone d'affichage du prompt (toujours visible) */}
                <Grid item xs={12} md={5}>
                  {selectedModel ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '600px' }}>
                      {/* Affichage du prompt - 50% de la hauteur */}
                      <Box sx={{ 
                        p: 3,
                        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '16px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        position: 'relative',
                        overflow: 'hidden',
                        height: '290px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2
                        }}>
              <Typography 
                            variant="subtitle2" 
                sx={{ 
                              color: 'rgba(248, 250, 252, 0.9)',
                              fontWeight: 600,
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            📝 Prompt sélectionné
              </Typography>
                          <Chip 
                            label={selectedModel.title}
              size="small"
              sx={{
                              background: 'rgba(79, 70, 229, 0.8)',
                color: 'white',
                              fontSize: '10px'
                            }}
                          />
                        </Box>
                        <Box sx={{ 
                          flex: 1, 
                          overflow: 'auto',
                          background: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          p: 2,
                          '&::-webkit-scrollbar': {
                            width: '4px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(148, 163, 184, 0.3)',
                            borderRadius: '2px',
                '&:hover': {
                              background: 'rgba(148, 163, 184, 0.5)',
                            },
                          },
                        }}>
                          <Typography 
                            component="pre"
                            sx={{ 
                              color: '#e2e8f0',
                              fontSize: { xs: '10px', sm: '11px' },
                              lineHeight: 1.4,
                              fontFamily: '"Fira Code", "JetBrains Mono", Consolas, monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              margin: 0
                            }}
                          >
                            {selectedModel.prompt}
                          </Typography>
          </Box>
        </Box>

                      {/* Instructions personnalisées - 50% de la hauteur */}
          <Box sx={{ 
                        p: 3,
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '16px',
                        border: '1px solid rgba(79, 70, 229, 0.2)',
                        boxShadow: '0 4px 20px rgba(79, 70, 229, 0.12)',
                        position: 'relative',
                        overflow: 'hidden',
                        height: '290px',
                        display: 'flex',
                        flexDirection: 'column',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(45deg, rgba(79, 70, 229, 0.02) 0%, transparent 50%, rgba(99, 102, 241, 0.02) 100%)',
                          zIndex: 0,
                        }
                      }}>
                        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography 
                            variant="subtitle2" 
              sx={{ 
                              mb: 2,
                              fontWeight: 700,
                              color: '#1e293b',
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                          >
                            🎯 Personnalisation du prompt
            </Typography>

                          <TextField
                            label="Ajustements et contexte supplémentaire"
                            name="userMessage"
                            value={generateSummaryData.userMessage || ''}
                            onChange={handleSummaryDataChange}
                            multiline
                            fullWidth
            variant="outlined"
                            placeholder="Peaufinez le prompt ci-dessus en apportant du contexte supplémentaire, vos attentes spécifiques, le niveau de détail souhaité, l'audience cible, ou tout autre élément pour personnaliser davantage votre résumé..."
            sx={{ 
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                                backdropFilter: 'blur(12px)',
                                height: '100%',
                                '& fieldset': {
                                  borderColor: 'rgba(79, 70, 229, 0.2)',
                                  borderWidth: 1.5
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(79, 70, 229, 0.4)'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'rgba(79, 70, 229, 0.6)',
                                  borderWidth: 2
                                }
                              },
                              '& .MuiInputBase-root': {
                                height: '100%',
                                alignItems: 'flex-start'
                              },
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '13px', sm: '14px' },
                                lineHeight: 1.5,
                                height: '100% !important',
                                overflow: 'auto !important'
                              },
                              '& .MuiInputBase-input::placeholder': {
                                fontSize: { xs: '13px', sm: '14px' },
                                opacity: 0.6,
                                color: '#64748b'
                              },
                              '& .MuiInputLabel-root': {
              fontWeight: 600,
                                color: '#475569',
                                fontSize: { xs: '13px', sm: '14px' },
                                '&.Mui-focused': {
                                  color: '#4f46e5'
                                }
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      p: 4,
                      background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
                      backdropFilter: 'blur(12px)',
                      borderRadius: '16px',
                      border: '2px dashed rgba(79, 70, 229, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      height: { xs: 'auto', md: '500px' },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px auto',
                          animation: 'pulse 2s ease-in-out infinite alternate'
                        }}>
                          <Typography sx={{ fontSize: '24px' }}>📝</Typography>
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: '#4f46e5',
                            fontWeight: 700,
                            mb: 1,
                            fontSize: { xs: '1rem', sm: '1.1rem' }
                          }}
                        >
                          Sélectionnez un modèle pour votre résumé
                        </Typography>
                        <Typography 
                          variant="body2" 
            sx={{ 
                            color: '#64748b',
                            lineHeight: 1.6,
                            mb: 3,
                            fontSize: { xs: '13px', sm: '14px' }
                          }}
                        >
                          Choisissez un modèle dans la liste de gauche pour voir son prompt personnalisé
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: 3,
                        background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '12px',
                        border: '1px solid rgba(79, 70, 229, 0.1)'
                      }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            color: '#1e293b',
              fontWeight: 600,
                            mb: 1.5,
                            fontSize: '14px'
                          }}
                        >
                          💡 Pourquoi un prompt bien rédigé est-il important ?
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#475569',
                            fontSize: '12px',
                            lineHeight: 1.5,
                            textAlign: 'left'
                          }}
                        >
                          • <strong>Précision :</strong> Guide l'IA pour générer exactement ce dont vous avez besoin<br/>
                          • <strong>Structure :</strong> Assure un format cohérent et professionnel<br/>
                          • <strong>Contexte :</strong> Adapte le résumé à votre audience et vos objectifs<br/>
                          • <strong>Qualité :</strong> Maximise la pertinence et la clarté du contenu généré
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>

          </Box>
        )}
      </BaseDialog>

      {/* Dialog d'envoi d'email pour le résumé */}
      <EmailDialog
        open={summaryEmailDialogOpen}
        onClose={handleCloseSummaryEmailDialog}
        meeting={meeting}
        summary={summary}
        summaryStats={summary}
        contentType="summary"
      />

      {/* Dialog d'envoi d'email pour la transcription */}
      <EmailDialog
        open={transcriptEmailDialogOpen}
        onClose={handleCloseTranscriptEmailDialog}
        meeting={meeting}
        transcript={transcription}
        contentType="transcript"
      />

      {/* Dialog d'édition de la réunion */}
      <EditMeetingDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={async (meetingId) => {
          setEditDialogOpen(false);
          await addNotification({
            message: '✅ Réunion modifiée avec succès',
            type: 'success',
            category: 'meeting',
            metadata: { meetingTitle: meeting?.title, meetingId }
          });
        }}
        meetingId={meeting?.id}
      />

      <ProjectMeetingsSelectionDialog
        open={projectMeetingsDialogOpen}
        onClose={handleProjectMeetingsDialogClose}
        onConfirm={handleProjectMeetingsSelected}
        projectId={meeting?.projectId}
        currentMeetingId={meeting?.id}
      />

      {/* Dialog de confirmation de suppression */}
      <BaseDialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        title="🗑️ Confirmer la suppression"
        subtitle="Cette action est irréversible"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
            <BaseButton 
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{ px: 4, py: 1 }}
            >
              Annuler
            </BaseButton>
            <BaseButton 
              onClick={handleConfirmDelete}
              variant="contained"
              sx={{ 
                px: 4, 
                py: 1,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                }
              }}
            >
              Supprimer
            </BaseButton>
          </Box>
        }
        customBackgroundContent="#fff5f5"
        sx={{
          '& .MuiDialog-paper': {
            border: '1px solid rgba(244, 67, 54, 0.2)',
          }
        }}
      >
        <Box sx={{ 
          textAlign: 'center',
          py: 2,
          background: 'linear-gradient(145deg, rgba(255,245,245,0.8) 0%, rgba(255,235,235,0.9) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(244, 67, 54, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#d32f2f' }}>
            ⚠️ Attention !
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Êtes-vous sûr de vouloir supprimer définitivement cette réunion ?<br/>
            <strong>Cette action ne peut pas être annulée.</strong>
          </Typography>
        </Box>
      </BaseDialog>
      
      {/* ChatBot IA - Uniquement si la transcription est disponible */}
                {transcription && (transcription.validationStatus === 'completed' || (transcription.status === 'completed' && !transcription.validationStatus)) && (
        <ChatBot meeting={meeting} transcription={transcription} />
      )}

      {/* Dialog d'erreur d'authentification mobile */}
      <BaseDialog
        open={showMobileAuthDialog}
        onClose={() => setShowMobileAuthDialog(false)}
        title="📱 Problème d'authentification mobile"
        subtitle="Reconnexion requise"
        maxWidth="sm"
        customBackgroundContent="#fff8f0"
        actions={
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
            <BaseButton 
              onClick={() => setShowMobileAuthDialog(false)}
              variant="outlined"
              sx={{ px: 3, py: 1 }}
            >
              Ignorer
            </BaseButton>
            <BaseButton 
              onClick={handleMobileReconnect}
              variant="contained"
              sx={{ 
                px: 3, 
                py: 1,
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                }
              }}
            >
              Se reconnecter
            </BaseButton>
          </Box>
        }
        sx={{
          '& .MuiDialog-paper': {
            border: '1px solid rgba(255, 152, 0, 0.2)',
          }
        }}
      >
        <Box sx={{ 
          textAlign: 'center',
          py: 2,
          background: 'linear-gradient(145deg, rgba(255,248,240,0.8) 0%, rgba(255,243,224,0.9) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 152, 0, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#f57c00', mb: 2 }}>
            ⚠️ Token Azure expiré
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, color: '#333' }}>
            {mobileAuthError?.message || 'Votre session Azure a expiré.'}
          </Typography>
          
          {isMobileDevice && (
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: 'rgba(255, 152, 0, 0.1)', 
              borderRadius: '12px',
              border: '1px solid rgba(255, 152, 0, 0.2)'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#f57c00', mb: 1 }}>
                📱 Conseil mobile :
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', lineHeight: 1.4 }}>
                Sur mobile, les popups de reconnexion peuvent être bloqués. 
                Utilisez le bouton "Se reconnecter" pour une authentification optimisée.
              </Typography>
            </Box>
          )}
          
          {mobileAuthError?.timestamp && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
              Détecté le {format(mobileAuthError.timestamp, "HH:mm:ss")}
            </Typography>
          )}
        </Box>
      </BaseDialog>
    </Box>
  );
};

// Ajouter cette fonction de formatage en dehors du composant principal
const formatTranscriptionSegments = (transcription) => {
  // Si la transcription a déjà des segments au format attendu, les retourner
  if (transcription.result && transcription.result.segments) {
    return transcription.result.segments;
  }
  
  // Si la transcription est au format utterances (avec diarisation)
  if (transcription.result && transcription.result.utterances) {
    return transcription.result.utterances.map((utterance, index) => ({
      speaker: utterance.speaker,
      text: utterance.text,
      timestamp: utterance.start || index * 5 // Utiliser start s'il existe, sinon une valeur approximative
    }));
  }
  
  // Si la transcription contient des mots individuels avec des locuteurs
  if (transcription.result && transcription.result.words && transcription.result.words.length > 0) {
    const segments = [];
    let currentSpeaker = null;
    let currentText = '';
    let currentTimestamp = 0;
    
    // Regrouper les mots par locuteur
    transcription.result.words.forEach((word, index) => {
      if (word.speaker !== currentSpeaker || index === 0) {
        if (currentSpeaker !== null) {
          segments.push({
            speaker: `Locuteur ${currentSpeaker}`,
            text: currentText.trim(),
            timestamp: currentTimestamp
          });
        }
        currentSpeaker = word.speaker;
        currentText = word.text;
        currentTimestamp = word.start || index * 0.5;
      } else {
        currentText += ' ' + word.text;
      }
    });
    
    // Ajouter le dernier segment
    if (currentSpeaker !== null && currentText.trim()) {
      segments.push({
        speaker: `Locuteur ${currentSpeaker}`,
        text: currentText.trim(),
        timestamp: currentTimestamp
      });
    }
    
    return segments;
  }
  
  // Si on a juste un texte plat, créer un segment unique
  if (transcription.fullText) {
    return [{
      speaker: 'Transcription',
      text: transcription.fullText,
      timestamp: 0
    }];
  }
  
  // Par défaut, retourner un tableau vide
  return [];
};

// Utilitaire : convertit un HTML simple (issu du markdown) en Paragraphs docx stylés
function htmlToDocxParagraphs(html) {
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = Array.from(doc.body.childNodes);
  const paragraphs = [];

  function parseNode(node) {
    if (node.nodeType === 3) { // text
      // Toujours encapsuler dans un Paragraph
      return [new Paragraph(node.textContent)];
    }
    if (node.nodeType !== 1) return [];
    const tag = node.tagName.toLowerCase();
    switch (tag) {
      case 'h1':
        return [new Paragraph({ text: node.textContent, heading: HeadingLevel.HEADING_1 })];
      case 'h2':
        return [new Paragraph({ text: node.textContent, heading: HeadingLevel.HEADING_2 })];
      case 'h3':
        return [new Paragraph({ text: node.textContent, heading: HeadingLevel.HEADING_3 })];
      case 'ul':
        return Array.from(node.children).map(li =>
          new Paragraph({
            text: li.textContent,
            bullet: { level: 0 },
          })
        );
      case 'ol':
        return Array.from(node.children).map((li, i) =>
          new Paragraph({
            children: [new TextRun({ text: `${i + 1}. `, bold: true }), new TextRun(li.textContent)],
          })
        );
      case 'p':
        return [new Paragraph(parseInline(node))];
      case 'blockquote':
        return [new Paragraph({
          children: [new TextRun({ text: node.textContent, italics: true, color: '888888' })],
        })];
      case 'pre':
        return [new Paragraph({
          children: [new TextRun({ text: node.textContent, font: 'Courier New' })],
        })];
      case 'hr':
        return [new Paragraph({ text: '---' })];
      default:
        return [new Paragraph(parseInline(node))];
    }
  }

  function parseInline(node) {
    const runs = [];
    node.childNodes.forEach(child => {
      if (child.nodeType === 3) {
        runs.push(new TextRun(child.textContent));
      } else if (child.nodeType === 1) {
        const tag = child.tagName.toLowerCase();
        switch (tag) {
          case 'strong':
          case 'b':
            runs.push(new TextRun({ text: child.textContent, bold: true }));
            break;
          case 'em':
          case 'i':
            runs.push(new TextRun({ text: child.textContent, italics: true }));
            break;
          case 'u':
            runs.push(new TextRun({ text: child.textContent, underline: {} }));
            break;
          case 'a':
            runs.push(new TextRun({ text: child.textContent, color: '0000FF', underline: {}, style: 'Hyperlink' }));
            break;
          case 'code':
            runs.push(new TextRun({ text: child.textContent, font: 'Courier New', color: '333333' }));
            break;
          default:
            runs.push(new TextRun(child.textContent));
        }
      }
    });
    return runs;
  }

  elements.forEach(el => {
    const result = parseNode(el);
    result.forEach(par => {
      if (par instanceof Paragraph) {
        paragraphs.push(par);
      }
    });
  });
  return paragraphs;
}

// Utilitaire pour créer un document Word pour le résumé avec un design moderne
async function createSummaryWordDocument(meeting, summary) {
  const font = 'Inter';
  const fallbackFont = 'Segoe UI, Roboto, Arial, sans-serif';
  const docChildren = [];
  
  // En-tête moderne avec icône et branding
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: '✨ TRANSCRIPTION AUTOMATIQUE',
          size: 16,
          font: fallbackFont,
          color: '6366F1',
          bold: true,
          allCaps: true,
        }),
      ],
    })
  );

  // Titre principal avec emoji
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '📋 ',
          size: 48,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'RÉSUMÉ DE RÉUNION',
          bold: true,
          size: 32,
          font: fallbackFont,
          color: '1E293B',
        }),
      ],
    })
  );

  // Titre de la réunion avec style moderne
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: meeting.title || 'Réunion sans titre',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '475569',
        }),
      ],
    })
  );

  // Ligne de séparation décorative
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Section informations avec icônes
  const infoDate = meeting.date ? format(new Date(meeting.date), "EEEE d MMMM yyyy", { locale: fr }) : 'Date non spécifiée';
  
  // Tableau d'informations stylé
  const infoRows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: '📅 Date',
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '3B82F6',
                }),
              ],
            })
          ],
          width: { size: 30, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
          shading: { fill: 'F8FAFC' },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: infoDate,
                  size: 20,
                  font: fallbackFont,
                  color: '64748B',
                }),
              ],
            })
          ],
          width: { size: 70, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
        }),
      ],
    }),
  ];

  // Ajouter la durée si disponible
  if (meeting.duration) {
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '⏱️ Durée',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '10B981',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F8FAFC' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: meeting.duration,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter les participants si disponibles
  if (meeting.participants && meeting.participants.length > 0) {
    const participantsText = meeting.participants
      .map(p => typeof p === 'string' ? p : p.displayName || p.name || 'Participant')
      .join(', ');
    
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '👥 Participants',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: 'F59E0B',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F8FAFC' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: participantsText,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter le tableau d'informations
  docChildren.push(
    new Table({
      rows: infoRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: 'single', size: 2, color: 'E2E8F0' },
        bottom: { style: 'single', size: 2, color: 'E2E8F0' },
        left: { style: 'single', size: 2, color: 'E2E8F0' },
        right: { style: 'single', size: 2, color: 'E2E8F0' },
        insideHorizontal: { style: 'single', size: 1, color: 'F1F5F9' },
        insideVertical: { style: 'single', size: 1, color: 'F1F5F9' },
      },
    })
  );

  // Espacement avant le contenu
  docChildren.push(
    new Paragraph({
      spacing: { before: 600, after: 400 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Titre de section résumé
  docChildren.push(
    new Paragraph({
      spacing: { before: 300, after: 300 },
      children: [
        new TextRun({
          text: '🎯 ',
          size: 28,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'RÉSUMÉ EXÉCUTIF',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '1E293B',
          allCaps: true,
        }),
      ],
    })
  );

  // Ligne sous le titre
  docChildren.push(
    new Paragraph({
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '▔'.repeat(50),
          size: 16,
          font: fallbackFont,
          color: '6366F1',
        }),
      ],
    })
  );

  // Contenu du résumé avec conversion markdown
  if (summary && summary.text) {
    try {
      const html = await markdownToHtml(summary.text);
      const summaryParagraphs = htmlToDocxParagraphsModern(html, fallbackFont);
      docChildren.push(...summaryParagraphs);
    } catch (error) {

      // Fallback: traiter comme du texte simple
      const lines = summary.text.split('\n').filter(line => line.trim());
      lines.forEach((line, index) => {
        // Détecter les titres markdown
        if (line.startsWith('# ')) {
          docChildren.push(
            new Paragraph({
              spacing: { before: 300, after: 200 },
              children: [
                new TextRun({
                  text: line.substring(2),
                  bold: true,
                  size: 22,
                  font: fallbackFont,
                  color: '1E293B',
                }),
              ],
            })
          );
        } else if (line.startsWith('## ')) {
          docChildren.push(
            new Paragraph({
              spacing: { before: 250, after: 150 },
              children: [
                new TextRun({
                  text: line.substring(3),
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '475569',
                }),
              ],
            })
          );
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          docChildren.push(
            new Paragraph({
              spacing: { after: 100 },
              indent: { left: 360 },
              children: [
                new TextRun({
                  text: '• ',
                  bold: true,
                  size: 18,
                  font: fallbackFont,
                  color: '6366F1',
                }),
                new TextRun({
                  text: line.substring(2),
                  size: 18,
                  font: fallbackFont,
                  color: '475569',
                }),
              ],
            })
          );
        } else if (line.trim()) {
          docChildren.push(
            new Paragraph({
              spacing: { after: 150 },
              children: [
                new TextRun({
                  text: line,
                  size: 18,
                  font: fallbackFont,
                  color: '475569',
                }),
              ],
            })
          );
        }
      });
    }
  } else {
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'Aucun résumé disponible pour cette réunion.',
            italics: true,
            size: 18,
            font: fallbackFont,
            color: '94A3B8',
          }),
        ],
      })
    );
  }

  // Espacement avant le pied de page
  docChildren.push(
    new Paragraph({
      spacing: { before: 800 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Ligne de séparation avant pied de page
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Pied de page élégant
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 200 },
      children: [
        new TextRun({
          text: '🤖 Document généré automatiquement par l\'IA de Transcription le ',
          size: 16,
          italics: true,
          font: fallbackFont,
          color: '64748B',
        }),
        new TextRun({
          text: format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr }),
          size: 16,
          italics: true,
          bold: true,
          font: fallbackFont,
          color: '6366F1',
        }),
      ],
    })
  );

  return new Document({
    styles: {
      default: {
        document: {
          run: {
            font: fallbackFont,
            size: 18,
            color: '475569',
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: docChildren,
      },
    ],
  });
}

// Utilitaire pour créer un document Word pour la transcription avec un design moderne
async function createTranscriptWordDocument(meeting, transcription) {
  const font = 'Inter';
  const fallbackFont = 'Segoe UI, Roboto, Arial, sans-serif';
  const docChildren = [];
  
  // En-tête moderne avec icône et branding
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: '🎙️ TRANSCRIPTION AUTOMATIQUE',
          size: 16,
          font: fallbackFont,
          color: '059669',
          bold: true,
          allCaps: true,
        }),
      ],
    })
  );

  // Titre principal avec emoji
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '📝 ',
          size: 48,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'TRANSCRIPTION COMPLÈTE',
          bold: true,
          size: 32,
          font: fallbackFont,
          color: '1E293B',
        }),
      ],
    })
  );

  // Titre de la réunion avec style moderne
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: meeting.title || 'Réunion sans titre',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '475569',
        }),
      ],
    })
  );

  // Ligne de séparation décorative
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Section informations avec icônes
  const infoDate = meeting.date ? format(new Date(meeting.date), "EEEE d MMMM yyyy", { locale: fr }) : 'Date non spécifiée';
  
  // Tableau d'informations stylé
  const infoRows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: '📅 Date',
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '10B981',
                }),
              ],
            })
          ],
          width: { size: 30, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
          shading: { fill: 'F0FDF4' },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: infoDate,
                  size: 20,
                  font: fallbackFont,
                  color: '64748B',
                }),
              ],
            })
          ],
          width: { size: 70, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
        }),
      ],
    }),
  ];

  // Ajouter la durée si disponible
  if (meeting.duration) {
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '⏱️ Durée',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '059669',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F0FDF4' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: meeting.duration,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter les participants si disponibles
  if (meeting.participants && meeting.participants.length > 0) {
    const participantsText = meeting.participants
      .map(p => typeof p === 'string' ? p : p.displayName || p.name || 'Participant')
      .join(', ');
    
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '👥 Participants',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '047857',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F0FDF4' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: participantsText,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter le nombre de segments
  const segments = formatTranscriptionSegments(transcription);
  if (segments && segments.length > 0) {
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '🎯 Segments',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '065F46',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F0FDF4' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${segments.length} segments de conversation`,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter le tableau d'informations
  docChildren.push(
    new Table({
      rows: infoRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: 'single', size: 2, color: 'D1FAE5' },
        bottom: { style: 'single', size: 2, color: 'D1FAE5' },
        left: { style: 'single', size: 2, color: 'D1FAE5' },
        right: { style: 'single', size: 2, color: 'D1FAE5' },
        insideHorizontal: { style: 'single', size: 1, color: 'ECFDF5' },
        insideVertical: { style: 'single', size: 1, color: 'ECFDF5' },
      },
    })
  );

  // Espacement avant le contenu
  docChildren.push(
    new Paragraph({
      spacing: { before: 600, after: 400 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Titre de section transcription
  docChildren.push(
    new Paragraph({
      spacing: { before: 300, after: 300 },
      children: [
        new TextRun({
          text: '💬 ',
          size: 28,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'CONVERSATION DÉTAILLÉE',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '1E293B',
          allCaps: true,
        }),
      ],
    })
  );

  // Ligne sous le titre
  docChildren.push(
    new Paragraph({
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '▔'.repeat(50),
          size: 16,
          font: fallbackFont,
          color: '10B981',
        }),
      ],
    })
  );

  // Fonction pour formater le timestamp
  const formatTimestamp = (ms) => {
    if (!ms && ms !== 0) return '';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Contenu de la transcription
  if (segments && segments.length > 0) {
    segments.forEach((segment, index) => {
      const speakerIcon = segment.speaker.includes('Locuteur') ? '👤' : '🎤';
      const timeDisplay = segment.timestamp !== undefined ? ` [${formatTimestamp(segment.timestamp * 1000)}]` : '';
      
      // En-tête du segment avec speaker et temps
      docChildren.push(
        new Paragraph({
          spacing: { before: index > 0 ? 300 : 0, after: 150 },
          children: [
            new TextRun({
              text: speakerIcon + ' ',
              size: 20,
              font: fallbackFont,
              color: '10B981',
            }),
            new TextRun({
              text: segment.speaker,
              bold: true,
              size: 20,
              font: fallbackFont,
              color: '047857',
            }),
            new TextRun({
              text: timeDisplay,
              size: 16,
              font: fallbackFont,
              color: '6B7280',
              italics: true,
            }),
          ],
        })
      );

      // Contenu du segment
      docChildren.push(
        new Paragraph({
          spacing: { after: 200 },
          indent: { left: 360 },
          children: [
            new TextRun({
              text: segment.text || 'Aucun contenu disponible',
              size: 18,
              font: fallbackFont,
              color: '374151',
            }),
          ],
        })
      );

      // Ligne de séparation entre segments (sauf le dernier)
      if (index < segments.length - 1) {
        docChildren.push(
          new Paragraph({
            spacing: { before: 100, after: 100 },
            children: [
              new TextRun({
                text: '━'.repeat(60),
                size: 12,
                font: fallbackFont,
                color: 'E5E7EB',
              }),
            ],
          })
        );
      }
    });
  } else {
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'Aucune transcription disponible pour cette réunion.',
            italics: true,
            size: 18,
            font: fallbackFont,
            color: '94A3B8',
          }),
        ],
      })
    );
  }

  // Espacement avant le pied de page
  docChildren.push(
    new Paragraph({
      spacing: { before: 800 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Ligne de séparation avant pied de page
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Pied de page élégant
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 200 },
      children: [
        new TextRun({
          text: '🤖 Document généré automatiquement par l\'IA de Transcription le ',
          size: 16,
          italics: true,
          font: fallbackFont,
          color: '64748B',
        }),
        new TextRun({
          text: format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr }),
          size: 16,
          italics: true,
          bold: true,
          font: fallbackFont,
          color: '10B981',
        }),
      ],
    })
  );

  return new Document({
    styles: {
      default: {
        document: {
          run: {
            font: fallbackFont,
            size: 18,
            color: '374151',
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: docChildren,
      },
    ],
  });
}

// Fonction moderne pour convertir le HTML (markdown) en paragraphes Word stylés
function htmlToDocxParagraphsModern(html, font = 'Segoe UI, Roboto, Arial, sans-serif') {
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = Array.from(doc.body.childNodes);
  const paragraphs = [];

  function parseNode(node) {
    if (node.nodeType === 3 && node.textContent.trim()) { // text node with content
      return [new Paragraph({
        children: [new TextRun({ text: node.textContent, font, size: 18, color: '4A5568' })],
        spacing: { after: 150 }
      })];
    }
    if (node.nodeType !== 1) return [];
    
    const tag = node.tagName.toLowerCase();
    const size = 18;
    
    switch (tag) {
      case 'h1':
        return [new Paragraph({
          children: [new TextRun({
            text: node.textContent,
            bold: true,
            size: 28,
            font,
            color: '1A202C'
          })],
          spacing: { before: 400, after: 300 }
        })];
        
      case 'h2':
        return [new Paragraph({
          children: [new TextRun({
            text: node.textContent,
            bold: true,
            size: 24,
            font,
            color: '2D3748'
          })],
          spacing: { before: 350, after: 250 }
        })];
        
      case 'h3':
        return [new Paragraph({
          children: [new TextRun({
            text: node.textContent,
            bold: true,
            size: 20,
            font,
            color: '4A5568'
          })],
          spacing: { before: 300, after: 200 }
        })];
        
      case 'ul':
        return Array.from(node.children).map(li =>
          new Paragraph({
            children: [
              new TextRun({ text: '• ', bold: true, color: '6366F1', font, size }),
              ...parseInline(li, font, size)
            ],
            indent: { left: 360 },
            spacing: { after: 150 }
          })
        );
        
      case 'ol':
        return Array.from(node.children).map((li, i) =>
          new Paragraph({
            children: [
              new TextRun({ text: `${i + 1}. `, bold: true, color: '6366F1', font, size }),
              ...parseInline(li, font, size)
            ],
            indent: { left: 360 },
            spacing: { after: 150 }
          })
        );
        
      case 'p':
        return [new Paragraph({
          children: parseInline(node, font, size),
          spacing: { after: 200 }
        })];
        
      case 'blockquote':
        return [new Paragraph({
          children: [new TextRun({
            text: node.textContent,
            italics: true,
            color: '718096',
            font,
            size: size - 2
          })],
          indent: { left: 720 },
          spacing: { after: 200 },
          shading: { fill: 'F7FAFC' }
        })];
        
      case 'pre':
      case 'code':
        return [new Paragraph({
          children: [new TextRun({
            text: node.textContent,
            font: 'Consolas, monospace',
            color: '2D3748',
            size: size - 2
          })],
          spacing: { after: 200 },
          shading: { fill: 'F7FAFC' }
        })];
        
      case 'table':
        return parseTableModern(node, font);
        
      case 'hr':
        return [new Paragraph({
          children: [new TextRun({
            text: '═'.repeat(50),
            color: 'E2E8F0',
            font,
            size: 14
          })],
          alignment: 'center',
          spacing: { before: 300, after: 300 }
        })];
        
      default:
        return [new Paragraph({
          children: parseInline(node, font, size),
          spacing: { after: 150 }
        })];
    }
  }

  function parseTableModern(tableNode, font) {
    const size = 16;
    try {
      const rows = Array.from(tableNode.querySelectorAll('tr'));
      const tableRows = rows.map((row, rowIndex) => {
        const cells = Array.from(row.querySelectorAll('td, th'));
        const isHeader = rowIndex === 0 || row.querySelector('th');
        
        const tableCells = cells.map(cell => new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: cell.textContent || '',
              font,
              size,
              bold: isHeader,
              color: isHeader ? '1A202C' : '4A5568'
            })]
          })],
          margins: { top: 200, bottom: 200, left: 200, right: 200 },
          shading: { fill: isHeader ? 'EDF2F7' : 'FFFFFF' }
        }));

        return new TableRow({ children: tableCells });
      });

      return [new Table({
        rows: tableRows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: 'single', size: 2, color: 'E2E8F0' },
          bottom: { style: 'single', size: 2, color: 'E2E8F0' },
          left: { style: 'single', size: 2, color: 'E2E8F0' },
          right: { style: 'single', size: 2, color: 'E2E8F0' },
          insideHorizontal: { style: 'single', size: 1, color: 'F1F5F9' },
          insideVertical: { style: 'single', size: 1, color: 'F1F5F9' },
        }
      })];
    } catch (error) {

      // Fallback : tableau en texte
      const rows = Array.from(tableNode.querySelectorAll('tr'));
      const paragraphs = [];
      
      rows.forEach((row, rowIndex) => {
        const cells = Array.from(row.querySelectorAll('td, th'));
        const cellTexts = cells.map(cell => cell.textContent || '').join(' | ');
        const isHeader = rowIndex === 0 || row.querySelector('th');
        
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: cellTexts,
            font,
            size: size - 2,
            bold: isHeader,
            color: isHeader ? '2D3748' : '4A5568'
          })],
          spacing: { after: 100 }
        }));
        
        if (isHeader) {
          paragraphs.push(new Paragraph({
            children: [new TextRun({ 
              text: '─'.repeat(cellTexts.length), 
              font, 
              size: size - 4, 
              color: 'E2E8F0' 
            })],
            spacing: { after: 100 }
          }));
        }
      });
      
      return paragraphs;
    }
  }

  function parseInline(node, font, size) {
    const runs = [];
    node.childNodes.forEach(child => {
      if (child.nodeType === 3) {
        runs.push(new TextRun({ text: child.textContent, font, size, color: '4A5568' }));
      } else if (child.nodeType === 1) {
        const tag = child.tagName.toLowerCase();
        switch (tag) {
          case 'strong':
          case 'b':
            runs.push(new TextRun({ text: child.textContent, bold: true, font, size, color: '2D3748' }));
            break;
          case 'em':
          case 'i':
            runs.push(new TextRun({ text: child.textContent, italics: true, font, size, color: '4A5568' }));
            break;
          case 'u':
            runs.push(new TextRun({ text: child.textContent, underline: {}, font, size, color: '4A5568' }));
            break;
          case 'a':
            runs.push(new TextRun({ text: child.textContent, color: '4299E1', underline: {}, font, size }));
            break;
          case 'code':
            runs.push(new TextRun({ text: child.textContent, font: 'Consolas, monospace', color: 'E53E3E', size: size - 2 }));
            break;
          default:
            runs.push(new TextRun({ text: child.textContent, font, size, color: '4A5568' }));
        }
      }
    });
    return runs;
  }

  elements.forEach(el => {
    const result = parseNode(el);
    result.forEach(par => {
      if (par instanceof Paragraph || par instanceof Table) {
        paragraphs.push(par);
      }
    });
  });
  return paragraphs;
}

export default MeetingDetailsPage; 